/**
 * English translations — en
 *
 * Flat key structure: feature.element or feature.element.detail
 * Keys use camelCase segments separated by dots.
 */

import type { TranslationDict } from './index';

export const en: TranslationDict = {
  // ============================================
  // Common / Shared
  // ============================================
  'common.save': 'Save',
  'common.saving': 'Saving...',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.deleting': 'Deleting...',
  'common.confirm': 'Confirm',
  'common.create': 'Create',
  'common.update': 'Update',
  'common.edit': 'Edit',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.retry': 'Retry',
  'common.refresh': 'Refresh',
  'common.loading': 'Loading...',
  'common.loadMore': 'Load More',
  'common.processing': 'Processing...',
  'common.search': 'Search',
  'common.reset': 'Reset',
  'common.resetFilters': 'Reset Filters',
  'common.clearFilters': 'Clear Filters',
  'common.clearSelection': 'Clear Selection',
  'common.viewAll': 'View all →',
  'common.showing': 'Showing',
  'common.of': 'of',
  'common.page': 'Page',
  'common.all': 'All',
  'common.active': 'Active',
  'common.pending': 'Pending',
  'common.completed': 'Completed',
  'common.cancelled': 'Cancelled',
  'common.unknown': 'Unknown',
  'common.na': 'N/A',
  'common.justNow': 'Just now',
  'common.minutesAgo': '{count}m ago',
  'common.hoursAgo': '{count}h ago',
  'common.daysAgo': '{count}d ago',
  'common.onThisPage': '{loaded} on this page, {total} total',
  'common.error': 'Error',
  'common.success': 'Success',
  'common.unlimited': 'Unlimited',
  'common.free': 'Free',
  'common.items': 'items',
  'common.item': 'item',
  'common.date': 'Date',
  'common.status': 'Status',
  'common.actions': 'Actions',
  'common.total': 'Total',
  'common.name': 'Name',
  'common.phone': 'Phone',
  'common.email': 'Email',
  'common.address': 'Address',
  'common.quantity': 'Quantity',
  'common.price': 'Price',
  'common.days': 'days',
  'common.unsavedChanges': 'Unsaved changes',
  'common.noResults': 'No results found',
  'common.somethingWentWrong': 'Something went wrong',
  'common.tryAgain': 'Try Again',
  'common.goBack': 'Go Back',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.or': 'or',
  'common.selected': 'selected',
  'common.advancedFilters': 'Advanced Filters',
  'common.dateFrom': 'Date From',
  'common.dateTo': 'Date To',
  'common.minPrice': 'Min Price',
  'common.maxPrice': 'Max Price',
  'common.sortBy': 'Sort By',
  'common.order': 'Order',

  // ============================================
  // Export Menu
  // ============================================
  'export.title': 'Export',
  'export.exporting': 'Exporting...',
  'export.csv': 'Export CSV',
  'export.csvDescription': 'Download data as spreadsheet',
  'export.printReceipts': 'Print Receipts',
  'export.printReceiptsDescription': 'Print selected as receipts',
  'export.printReport': 'Print Report',
  'export.printReportDescription': 'Print selected as A4 report',
  'export.printLabels': 'Print Labels',
  'export.printLabelsDescription': 'Print barcode / QR labels',
  'export.downloadPdf': 'Download PDF',
  'export.downloadPdfDescription': 'Save as PDF document',
  'export.actions': 'Actions',
  'export.download': 'Download',
  'export.print': 'Print',
  'export.printGroup': 'Print ({count} selected)',
  'export.noSelection': 'Select items first',

  // ============================================
  // Auth — Login
  // ============================================
  'auth.signIn': 'Sign In',
  'auth.signInDescription': 'Enter your credentials to access your account',
  'auth.sessionReplaced':
    'You were signed out because your account was signed in from another location. Please log in again.',
  'auth.emailLabel': 'Email',
  'auth.emailPlaceholder': 'you@example.com',
  'auth.passwordLabel': 'Password',
  'auth.passwordPlaceholder': '••••••••',
  'auth.signingIn': 'Signing in...',
  'auth.forgotPassword': 'Forgot password?',
  'auth.noAccount': "Don't have an account?",
  'auth.signUp': 'Sign up',
  'auth.loginSuccess': 'Welcome back! You have successfully logged in.',
  'auth.loginSuccessTitle': 'Login Successful',
  'auth.loginFailed': 'Login failed. Please try again.',
  'auth.loginFailedTitle': 'Login Failed',

  // Auth — Signup
  'auth.createAccount': 'Create Account',
  'auth.signupDescription':
    'Enter your email to get started with Store Manager',
  'auth.checkEmail': 'Check your email',
  'auth.checkEmailSent': 'We sent a verification link to {email}.',
  'auth.checkEmailClick': 'Click the link to complete your registration.',
  'auth.checkEmailExpiry': 'The link expires in 72 hours.',
  'auth.sending': 'Sending...',
  'auth.continueWithEmail': 'Continue with Email',
  'auth.resendCooldown': 'Resend available in {seconds}s',
  'auth.resendEmail': "Didn't get the email? Resend",
  'auth.haveAccount': 'Already have an account?',
  'auth.signInLink': 'Sign in',
  'auth.validationEnterEmail': 'Please enter your email address',
  'auth.validationErrorTitle': 'Validation Error',
  'auth.emailSent': 'Check your inbox for a verification link!',
  'auth.emailSentTitle': 'Email Sent',
  'auth.signupFailed': 'Signup failed. Please try again.',
  'auth.signupFailedTitle': 'Signup Failed',
  'auth.unexpectedError': 'An unexpected error occurred',
  'auth.emailResent': 'Verification email resent!',
  'auth.emailResentTitle': 'Email Resent',
  'auth.resendFailed': 'Failed to resend email. Please try again.',
  'auth.resendFailedTitle': 'Resend Failed',
  'auth.termsAcknowledgment':
    'By signing up, you acknowledge that you agree to our',
  'auth.termsOfService': 'Terms of Service',
  'auth.privacyPolicy': 'Privacy Policy',
  'auth.and': 'and',
  'auth.visual.tagline': 'Manage smarter, grow faster.',
  'auth.visual.description':
    'Real-time inventory tracking, analytics, and team collaboration — all in one place.',

  // Auth — Onboarding
  'auth.onboarding.stepPersonal': 'Personal Details',
  'auth.onboarding.stepBusiness': 'Business Info',
  'auth.onboarding.stepStorehouse': 'Storehouse',
  'auth.onboarding.joinTeam': 'Join {businessName}',
  'auth.onboarding.joinTeamGeneric': 'Join Your Team',
  'auth.onboarding.completeRegistration': 'Complete Your Registration',
  'auth.onboarding.invalidLink': 'Invalid Verification Link',
  'auth.onboarding.completeProfile': 'Complete your profile for {email}',
  'auth.onboarding.settingUpAccount': 'Setting up account for {email}',
  'auth.onboarding.stepIndicator': 'Step {step}: {label}',
  'auth.onboarding.verifying': 'Verifying your link...',
  'auth.onboarding.fullName': 'Full Name',
  'auth.onboarding.birthDate': 'Birth Date',
  'auth.onboarding.phoneOptional': 'Phone Number (Optional)',
  'auth.onboarding.password': 'Password',
  'auth.onboarding.confirmPassword': 'Confirm Password',
  'auth.onboarding.businessName': 'Business Name',
  'auth.onboarding.businessAddress': 'Business Address',
  'auth.onboarding.businessPhone': 'Business Phone',
  'auth.onboarding.businessEmailOptional': 'Business Email (Optional)',
  'auth.onboarding.storehouseNameOptional': 'Storehouse Name (Optional)',
  'auth.onboarding.storehouseAddress': 'Storehouse Address',
  'auth.onboarding.storehousePhoneOptional': 'Storehouse Phone (Optional)',
  'auth.onboarding.storehouseEmailOptional': 'Storehouse Email (Optional)',
  'auth.onboarding.ageRequirement': 'You must be at least 18 years old',
  'auth.onboarding.passwordRequirement':
    'Min 8 chars with uppercase, lowercase, and number',
  'auth.onboarding.namePlaceholder': 'John Doe',
  'auth.onboarding.phonePlaceholder': '+1234567890',
  'auth.onboarding.businessNamePlaceholder': 'My Store',
  'auth.onboarding.businessAddressPlaceholder': '123 Main St',
  'auth.onboarding.businessPhonePlaceholder': '+1 234 567 8900',
  'auth.onboarding.businessEmailPlaceholder': 'contact@mybusiness.com',
  'auth.onboarding.storehouseNamePlaceholder': 'Main Warehouse',
  'auth.onboarding.storehouseAddressPlaceholder': '456 Warehouse Ave',
  'auth.onboarding.storehousePhonePlaceholder': '+1 234 567 8901',
  'auth.onboarding.storehouseEmailPlaceholder': 'warehouse@mybusiness.com',
  'auth.onboarding.joining': 'Joining...',
  'auth.onboarding.joinTeamBtn': 'Join Team',
  'auth.onboarding.creating': 'Creating...',
  'auth.onboarding.completeSetup': 'Complete Setup',
  'auth.onboarding.signUpAgain': 'Sign Up Again',
  'auth.onboarding.validationName': 'Please enter your name',
  'auth.onboarding.validationBirthDate': 'Please enter your birth date',
  'auth.onboarding.validationAge': 'You must be at least 18 years old',
  'auth.onboarding.validationPasswordLength':
    'Password must be at least 8 characters',
  'auth.onboarding.validationPasswordStrength':
    'Password must contain at least one uppercase letter, one lowercase letter, and one number',
  'auth.onboarding.validationPasswordMatch': 'Passwords do not match',
  'auth.onboarding.validationBusinessName': 'Please enter a business name',
  'auth.onboarding.validationBusinessAddress':
    'Please enter a business address',
  'auth.onboarding.validationBusinessPhone':
    'Please enter a business phone number',
  'auth.onboarding.validationStorehouseAddress':
    'Please enter a storehouse address',
  'auth.onboarding.welcomeMessage':
    'Your account has been created! Redirecting to dashboard...',
  'auth.onboarding.welcomeTitle': 'Welcome!',
  'auth.onboarding.registrationFailed':
    'Registration failed. Please try again.',
  'auth.onboarding.registrationFailedTitle': 'Registration Failed',
  'auth.onboarding.joinWelcome':
    'Welcome to {businessName}! Redirecting to dashboard...',
  'auth.onboarding.noToken':
    'No verification token provided. Please check your email link.',
  'auth.onboarding.invalidToken': 'Invalid or expired verification link.',
  'auth.onboarding.validationTitle': 'Validation',

  // Auth — Forgot Password
  'auth.forgotPasswordTitle': 'Forgot Password',
  'auth.forgotPasswordDescription':
    "Enter your email and we'll send you a link to reset your password",
  'auth.sendResetLink': 'Send Reset Link',
  'auth.backToSignIn': 'Back to Sign In',
  'auth.rememberPassword': 'Remember your password?',
  'auth.forgotPasswordSuccess':
    'If an account with that email exists, a password reset link has been sent. Please check your inbox.',
  'auth.forgotPasswordError': 'Something went wrong. Please try again later.',
  'auth.forgotPasswordErrorTitle': 'Error',

  // Auth — Reset Password
  'auth.resetPasswordTitle': 'Reset Password',
  'auth.resetPasswordDescription': 'Enter your new password below',
  'auth.invalidLink': 'Invalid Link',
  'auth.invalidLinkDescription':
    'This password reset link is invalid or missing a token.',
  'auth.newPassword': 'New Password',
  'auth.confirmPasswordLabel': 'Confirm Password',
  'auth.resetting': 'Resetting...',
  'auth.resetPassword': 'Reset Password',
  'auth.requestNewLink': 'Request a new link',
  'auth.resetPasswordsMismatch': 'Passwords do not match.',
  'auth.resetPasswordLength': 'Password must be at least 8 characters.',
  'auth.resetPasswordStrength':
    'Password must contain at least one uppercase letter, one lowercase letter, and one number.',
  'auth.resetTokenMissing': 'Reset token is missing.',
  'auth.resetTokenExpired':
    'This reset link is invalid or has expired. Please request a new one.',
  'auth.resetGenericError': 'Something went wrong. Please try again.',
  'auth.resetSuccess':
    'Your password has been reset successfully. Please log in.',
  'auth.resetSuccessTitle': 'Password Reset',

  // ProtectedRoute
  'auth.loadingSession': 'Loading...',

  // NoBusiness page
  'auth.noBusiness.title': 'No Business',
  'auth.noBusiness.description':
    'Your account is not connected to any business. This can happen if the business owner deleted it or your account was removed.',
  'auth.noBusiness.createTitle': 'Create a Business',
  'auth.noBusiness.createDescription':
    'Start fresh by creating your own business.',
  'auth.noBusiness.createButton': 'Create Business',
  'auth.noBusiness.joinTitle': 'Join a Business',
  'auth.noBusiness.joinDescription':
    'Ask a business admin to invite you by email. Click the link in the invitation email to join automatically, or paste the token below.',
  'auth.noBusiness.tokenPlaceholder': 'Paste invitation token…',
  'auth.noBusiness.joinButton': 'Join Business',
  'auth.noBusiness.joinSuccess': 'Successfully joined business!',
  'auth.noBusiness.joinFailed': 'Failed to join business',
  'auth.noBusiness.or': 'or',
  'auth.noBusiness.loggedInAs': 'Logged in as {email}',
  'auth.noBusiness.logout': 'Log out',

  // SetupBusiness page
  'auth.setup.title': 'Create Your Business',
  'auth.setup.description': 'Set up your business to get started.',
  'auth.setup.nameLabel': 'Business Name',
  'auth.setup.namePlaceholder': 'e.g. My Store',
  'auth.setup.addressLabel': 'Address',
  'auth.setup.addressPlaceholder': 'e.g. 123 Main Street',
  'auth.setup.phoneLabel': 'Phone Number',
  'auth.setup.phonePlaceholder': 'e.g. +1 555-0100',
  'auth.setup.emailLabel': 'Business Email (optional)',
  'auth.setup.emailPlaceholder': 'e.g. contact@mystore.com',
  'auth.setup.storehouseLabel': 'First Storehouse Name',
  'auth.setup.storehousePlaceholder': 'e.g. Main Warehouse',
  'auth.setup.submit': 'Create Business',
  'auth.setup.creating': 'Creating…',
  'auth.setup.success': 'Business created successfully!',
  'auth.setup.failed': 'Failed to create business',
  'auth.setup.back': 'Back',

  // ============================================
  // Navigation / Layout
  // ============================================
  'nav.inventory': 'Inventory',
  'nav.dashboard': 'Dashboard',
  'nav.orders': 'Orders',
  'nav.imports': 'Imports',
  'nav.dataImport': 'Bulk Import',
  'nav.transfers': 'Transfers',
  'nav.clients': 'Clients',
  'nav.suppliers': 'Suppliers',
  'nav.analytics': 'Analytics',
  'nav.activity': 'Activity',
  'nav.auditLog': 'Audit Log',
  'nav.storeManager': 'Store Manager',
  'nav.toggleMenu': 'Toggle mobile menu',
  'nav.switchToLight': 'Switch to light mode',
  'nav.switchToDark': 'Switch to dark mode',
  'nav.profile': 'Profile',
  'nav.businessSettings': 'Business Settings',
  'nav.teamManagement': 'Team Management',
  'nav.billing': 'Billing',
  'nav.devPortal': 'Dev Portal',
  'nav.requestsInbox': 'Requests Inbox',
  'nav.translations': 'Translations',
  'nav.riskAnalytics': 'Risk Analytics',
  'nav.stockIntelligence': 'Stock Intelligence',
  'nav.landing': 'Home',
  'seo.landing.title':
    'Stock & Inventory Management for Warehouses, Stores & Teams',
  'nav.logout': 'Logout',
  'nav.requiresPlan': 'Requires {plan} plan',

  // ============================================
  // Home / Dashboard
  // ============================================
  'home.welcomeBack': 'Welcome back, {name}!',
  'home.welcomeUser': 'User',
  'home.subtitle': 'Quick overview of your store operations',
  'home.totalItems': 'Total Items',
  'home.lowStockItems': 'Low Stock Items',
  'home.pendingOrders': 'Pending Orders',
  'home.activeImports': 'Active Imports',
  'home.lowStockAlert': 'Low Stock Alert',
  'home.recentImports': 'Recent Imports',
  'home.recentOrders': 'Recent Orders',
  'home.quickRestock': 'Quick Restock',
  'home.allWellStocked': 'All items are well stocked!',
  'home.noRecentImports': 'No recent imports',
  'home.noRecentOrders': 'No recent orders',
  'home.stock': 'Stock: {quantity} {unit}',
  'home.lowStock': 'Low Stock',
  'home.urgencyCritical': 'Critical',
  'home.urgencyUrgent': 'Urgent',
  'home.urgencyWatch': 'Watch',
  'home.orderBefore': '{days}d left',
  'home.unknownSupplier': 'Unknown Supplier',
  'home.unknownClient': 'Unknown Client',
  'home.viewLowStock': 'View all {count} low stock items →',
  'home.nItems': '{count} item',
  'home.nItemsPlural': '{count} items',

  // ============================================
  // Not Found (404)
  // ============================================
  'notFound.title': '404',
  'notFound.subtitle': 'Page not found',
  'notFound.description':
    "The page you're looking for doesn't exist or has been moved.",
  'notFound.backHome': 'Back to Home',

  // ============================================
  // Error Boundary
  // ============================================
  'errorBoundary.unexpectedError': 'An unexpected error occurred',
  'errorBoundary.title': 'Something went wrong',
  'errorBoundary.tryAgain': 'Try Again',

  // ============================================
  // Data Import (Bulk Onboarding)
  // ============================================
  'dataImport.title': 'Bulk Import',
  'dataImport.subtitle':
    'Upload a CSV file to bulk-create items or partners. Download a template, fill it in, then preview before committing.',
  'dataImport.supportNote':
    'If your data format is different and you need help importing, feel free to email us and describe your issue. We will prioritize support right away at',
  'dataImport.entity': 'Entity',
  'dataImport.entity.items': 'Items',
  'dataImport.entity.partners': 'Partners (clients & suppliers)',
  'dataImport.downloadTemplate': 'Get template',
  'dataImport.chooseFile': 'Choose file',
  'dataImport.preview': 'Preview',
  'dataImport.previewing': 'Previewing…',
  'dataImport.commit': 'Confirm import',
  'dataImport.committing': 'Importing…',
  'dataImport.cancel': 'Cancel',
  'dataImport.cancelling': 'Cancelling…',
  'dataImport.startOver': 'Start over',
  'dataImport.totalRows': 'Total rows',
  'dataImport.validRows': 'Valid rows',
  'dataImport.errorRows': 'Rows with errors',
  'dataImport.skippedRows': 'Skipped (duplicates)',
  'dataImport.skippedNote':
    'Some rows were skipped because matching records already exist and duplicate handling is set to Ignore.',
  'dataImport.errorsTitle': 'Errors found',
  'dataImport.errorsHelp':
    'Fix these rows in your CSV and re-upload, or proceed to import only the valid rows.',
  'dataImport.errorRow': 'Row',
  'dataImport.errorField': 'Field',
  'dataImport.errorMessage': 'Message',
  'dataImport.noFile': 'Please choose a CSV file first.',
  'dataImport.previewSuccess': 'Preview ready — review and confirm.',
  'dataImport.commitSuccess': 'Imported {count} rows successfully.',
  'dataImport.commitNothing':
    'Nothing to import — all rows have errors. Fix and re-upload.',
  'dataImport.cancelled': 'Import cancelled.',
  'dataImport.fileLabel': 'File',
  'dataImport.statusLabel': 'Status',
  'dataImport.fileSize': '{size} KB',
  'dataImport.dropHere': 'Drop your CSV file here',
  'dataImport.dropOrChoose': 'Drag & drop a CSV file here, or click to browse',
  'dataImport.fileHint': 'Accepts .csv only. Max 10,000 rows per import.',
  'dataImport.parsing': 'Reading file…',
  'dataImport.parseFailed':
    'Could not read this file. Make sure it is a valid CSV file.',
  // Column mapping
  'dataImport.mappingTitle': 'Map columns',
  'dataImport.mappingHint':
    'Match each column from your file to the destination field. Columns set to “Ignore” are dropped before import.',
  'dataImport.mappingSource': 'Your column',
  'dataImport.mappingTarget': 'Maps to',
  'dataImport.mappingIgnore': '— Ignore —',
  'dataImport.mappingEmpty': 'Map at least one column before previewing.',
  // Per-row selection
  'dataImport.selectAll': 'Select all rows in this group',
  'dataImport.selectedRows': 'Selected',
  'dataImport.commitSelected': 'Import {count} selected',
  // Failed-rows export
  'dataImport.downloadErrors': 'Download failed rows (CSV)',
  'dataImport.downloadingErrors': 'Preparing download…',
  // Auto-create storehouses (admin only)
  'dataImport.autoCreateStorehouses': 'Auto-create missing storehouses (admin)',
  'dataImport.autoCreateStorehousesHint':
    'When enabled, any storeHouseName in your file that does not yet exist will be created automatically with a placeholder address you can edit later.',
  'dataImport.onDuplicateItem': 'When duplicate item exists',
  'dataImport.onDuplicateItem.ignore': 'Ignore new row',
  'dataImport.onDuplicateItem.add': 'Add imported quantity to existing',
  'dataImport.onDuplicateItem.replace': 'Replace existing quantity',
  'dataImport.onDuplicateItemHint':
    'Duplicates are matched by SKU first, then by item name within the same storehouse.',
  'dataImport.pendingStorehousesNote':
    '{count} storehouse(s) will be created on import: {names}',
  'dataImport.removeFile': 'Remove file',
  'dataImport.replaceFile': 'Replace file',
  // Status badges
  'dataImport.status.previewed': 'Previewed',
  'dataImport.status.committed': 'Committed',
  'dataImport.status.failed': 'Failed',
  'dataImport.status.cancelled': 'Cancelled',
  // Preview tabs
  'dataImport.previewTabs.errors': 'Errors ({count})',
  'dataImport.previewTabs.byStoreHouse': 'By storehouse',
  'dataImport.previewTabs.byPartnerType': 'By partner type',
  'dataImport.noValidRows': 'No valid rows to preview.',
  'dataImport.unassignedStoreHouse': 'No storehouse specified',
  'dataImport.unassignedWarning':
    "Items without a storehouse assigned. Each item must belong to an existing storehouse — check your CSV's storeHouseName column.",
  'dataImport.partnerType.supplier': 'Suppliers',
  'dataImport.partnerType.client': 'Clients',
  'dataImport.partnerType.unknown': 'Other',
  'dataImport.itemsCount': '{count} items',
  'dataImport.partnersCount': '{count} partners',
  // Item columns
  'dataImport.col.name': 'Name',
  'dataImport.col.sku': 'SKU',
  'dataImport.col.unitPrice': 'Unit price',
  'dataImport.col.quantity': 'Qty',
  'dataImport.col.unit': 'Unit',
  'dataImport.col.lowStockAt': 'Low-stock at',
  // Partner columns
  'dataImport.col.partnerName': 'Name',
  'dataImport.col.partnerType': 'Type',
  'dataImport.col.phoneNumber': 'Phone',
  'dataImport.col.email': 'Email',
  'dataImport.col.address': 'Address',

  // ============================================
  // Inventory
  // ============================================
  'inventory.title': 'Inventory',
  'inventory.subtitle':
    'Track and manage your stock levels across all locations',
  'inventory.addNewItem': 'Add New Item',
  'inventory.exportCSV': 'Export CSV',
  'inventory.exporting': 'Exporting...',
  'inventory.totalItems': 'Total Items',
  'inventory.inventoryValue': 'Inventory Value',
  'inventory.outOfStock': 'Out of Stock',
  'inventory.lowStock': 'Low Stock',
  'inventory.inStock': 'In Stock',
  'inventory.loadingInventory': 'Loading inventory...',
  'inventory.showingItems': 'Showing {from} of {total} items',
  'inventory.pageOf': 'Page {page} of {pages}',

  // Inventory — Add/Edit Item
  'inventory.editItem': 'Edit Item',
  'inventory.itemDetails': 'Item Details',
  'inventory.viewItemInfo': 'View item information',
  'inventory.updateItemInfo': 'Update item information',
  'inventory.itemName': 'Item Name',
  'inventory.itemNamePlaceholder': 'Start typing item name...',
  'inventory.similarItemFound': 'Similar item found',
  'inventory.similarItemsFound': 'Similar items found',
  'inventory.autofillSuggestion': 'Similar items found. Click to autofill:',
  'inventory.conflictWarning':
    'Consider using the existing item or choose a different name to avoid confusion.',
  'inventory.description': 'Description',
  'inventory.descriptionPlaceholder': 'Item description...',
  'inventory.sku': 'SKU',
  'inventory.skuPlaceholder': 'e.g., WH-BOLT-M8, 8901234567890',
  'inventory.skuHint': 'Optional product code for barcode scanning',
  'inventory.generateSku': 'Generate',
  'inventory.unitPrice': 'Unit Price',
  'inventory.initialQuantity': 'Initial Quantity',
  'inventory.lowStockAlert': 'Low Stock Alert',
  'inventory.alertThresholdHint': 'Alert when stock falls below this number',
  'inventory.unit': 'Unit',
  'inventory.unitPlaceholder': 'e.g., pcs, box, kg...',
  'inventory.existingUnits': 'Existing units:',
  'inventory.storehouse': 'Storehouse',
  'inventory.selectStorehouse': 'Select storehouse...',
  'inventory.selectAStorehouse': 'Select a storehouse',
  'inventory.origin': 'Origin',
  'inventory.originPlaceholder': 'e.g., China, Taiwan, USA',
  'inventory.tags': 'Tags',
  'inventory.tagsPlaceholder': 'Search or type a tag...',
  'inventory.addMoreTags': 'Add more tags...',
  'inventory.tagsHint': 'Press Enter or comma to add a custom tag',
  'inventory.separateTagsCommas': 'Separate tags with commas',
  'inventory.createItem': 'Create Item',
  'inventory.creating': 'Creating...',
  'inventory.itemCreatedSuccess': 'Item created successfully!',
  'inventory.itemUpdatedSuccess': '{name} has been updated successfully',
  'inventory.itemUpdated': 'Item updated',

  // Inventory — Details View
  'inventory.name': 'Name',
  'inventory.quantity': 'Quantity',
  'inventory.location': 'Location',
  'inventory.itemCode': 'Item Code',
  'inventory.qr': 'QR',
  'inventory.barcode': 'Barcode',
  'inventory.barcodePrintComingSoon':
    'Barcode & QR label printing — coming soon',
  'inventory.reorderLevel': 'Reorder Level',
  'inventory.tagsCategories': 'Tags/Categories',
  'inventory.addTag': 'Add a tag...',
  'inventory.add': 'Add',
  'inventory.saving': 'Saving...',
  'inventory.saveChanges': 'Save Changes',
  'inventory.transfer': 'Transfer',
  'inventory.printLabel': 'Print Label',

  // Inventory — Transfer
  'inventory.transferStock': 'Transfer Stock',
  'inventory.moveToStorehouse': 'Move {itemName} to another storehouse',
  'inventory.currentLocation': 'Current Location',
  'inventory.unknown': 'Unknown',
  'inventory.availableStock': 'Available Stock',
  'inventory.destinationStorehouse': 'Destination Storehouse',
  'inventory.quantityToTransfer': 'Quantity to Transfer',
  'inventory.fullTransferWarning':
    'Full transfer — item will be moved entirely',
  'inventory.partialTransferInfo':
    'Partial transfer — {qty} {unit} will be moved, {remaining} remains',
  'inventory.note': 'Note',
  'inventory.noteOptional': '(optional)',
  'inventory.reasonPlaceholder': 'Reason for transfer...',
  'inventory.transferNow': 'Transfer Now',
  'inventory.moveImmediately': 'Move stock immediately',
  'inventory.pendingTransfer': 'Pending',
  'inventory.createRequestLater': 'Create request, complete later',
  'inventory.transferring': 'Transferring...',
  'inventory.createTransferRequest': 'Create Transfer Request',
  'inventory.stockTransferredSuccess': 'Stock transferred successfully',
  'inventory.transferRequestCreated':
    'Transfer request created (pending approval)',

  // Inventory — Filters
  'inventory.searchPlaceholder': 'Search by name, description, or tags...',
  'inventory.stock': 'Stock:',
  'inventory.allStock': 'All',
  'inventory.locationFilter': 'Location:',
  'inventory.allLocations': 'All Locations',
  'inventory.tagsFilter': 'Tags:',
  'inventory.loadingTags': 'Loading tags...',
  'inventory.noTagsYet': 'No tags available yet',

  // Inventory — Table
  'inventory.item': 'Item',
  'inventory.category': 'Category',
  'inventory.stockStatus': 'Stock Status',
  'inventory.cost': 'Cost',
  'inventory.lastUpdated': 'Last Updated',
  'inventory.unitLabel': 'Unit: {unit}',
  'inventory.noItemsFound': 'No items found',
  'inventory.adjustFiltersHint': 'Try adjusting your filters or search query',
  'inventory.nItemsSelected': '{count} item(s) selected',
  'inventory.inStockCount': '{count} in stock',
  'inventory.importStock': 'Import Stock',
  'inventory.createImportOrder': 'Create import order',
  'inventory.viewDetails': 'View Details',
  'inventory.seeFullInfo': 'See full information',
  'inventory.generateBarcodeQr': 'Generate barcode / QR label',
  'inventory.printAll': 'Print All ({count})',
  'inventory.printAllDescription': 'Print labels for all visible items',
  'inventory.printSelected': 'Print Selected',

  // Inventory — Print Labels
  'inventory.printLabels': 'Print Labels',
  'inventory.nItems': '{count} item(s)',
  'inventory.nLabels': '{count} / {max} label(s)',
  'inventory.maxLabelsReached': 'Maximum label limit reached',
  'inventory.codeType': 'Code Type',
  'inventory.qrCode': 'QR Code',
  'inventory.labelSize': 'Label Size',
  'inventory.itemsCopies': 'Items & Copies',
  'inventory.searchItems': 'Search items…',
  'inventory.noMatchSearch': 'No items match "{term}"',
  'inventory.qty': 'Qty: {qty}',
  'inventory.preview': 'Preview',
  'inventory.andMoreItems': '… and {count} more items',
  'inventory.downloadPdf': 'Download PDF',
  'inventory.generatingPdf': 'Generating…',
  'inventory.codeGenerationError': 'Failed to generate code',

  // Inventory — Stock Adjustment
  'inventory.adjustStock': 'Adjust Stock',
  'inventory.stockAdjustedSuccess': 'Stock adjusted successfully!',
  'inventory.currentStock': 'Current Stock',
  'inventory.addStock': '+ Add Stock',
  'inventory.removeStock': '− Remove Stock',
  'inventory.newStockHint': 'New stock: {qty} {unit}',
  'inventory.reason': 'Reason',
  'inventory.supplierDelivery': 'Supplier Delivery',
  'inventory.customerReturn': 'Customer Return',
  'inventory.manualCount': 'Manual Count Correction',
  'inventory.sale': 'Sale',
  'inventory.damagedDefective': 'Damaged/Defective',
  'inventory.theftLoss': 'Theft/Loss',
  'inventory.notesOptional': 'Notes (optional)',
  'inventory.notesPlaceholder': 'Add any additional notes...',
  'inventory.adjusting': 'Adjusting...',
  'inventory.confirmAdjustment': 'Confirm Adjustment',
  'inventory.validQuantityRequired':
    'Please enter a valid quantity greater than 0',
  'inventory.cannotRemoveMore':
    'Cannot remove {qty} units. Only {available} available.',

  // Inventory — Create Import
  'inventory.createImportFor': 'Create import order for {itemName}',
  'inventory.noStorehouse': 'No storehouse',
  'inventory.supplierOptional': 'Supplier (optional)',
  'inventory.noSupplierOther': 'No supplier / Other',
  'inventory.leaveEmptyInternal':
    'Leave empty for internal transfers or non-supplier imports',
  'inventory.pricePerUnit': 'Price per {unit} from supplier',
  'inventory.newStockWillBe': 'New stock will be: {qty} {unit}',
  'inventory.totalAmount': 'Total Amount',
  'inventory.importCreatedSuccess': 'Import created for {itemName}',
  'inventory.importCreated': 'Import created',

  // ============================================
  // Orders
  // ============================================
  'orders.title': 'Orders',
  'orders.subtitle': 'Track and manage sales transactions with your clients',
  'orders.newOrder': 'New Order',
  'orders.exportCSV': 'Export CSV',
  'orders.exporting': 'Exporting...',
  'orders.allOrders': 'All Orders',
  'orders.validationMinItem': 'At least one valid item is required',

  // Orders — Filters
  'orders.searchPlaceholder': 'Search by ID, client, email, phone...',
  'orders.allClients': 'All Clients',
  'orders.sortDate': 'Date',
  'orders.sortTotalPrice': 'Total Price',
  'orders.sortClientName': 'Client Name',
  'orders.sortNewestFirst': 'Newest First',
  'orders.sortOldestFirst': 'Oldest First',
  'orders.sortHighToLow': 'High to Low',
  'orders.sortLowToHigh': 'Low to High',
  'orders.client': 'Client',

  // Orders — Table
  'orders.loadingOrders': 'Loading orders...',
  'orders.failedToLoad': 'Failed to load orders',
  'orders.noMatch': 'No orders match your search',
  'orders.noOrders': 'No orders found',
  'orders.nSelected': '{count} order(s) selected',
  'orders.orderId': 'Order ID',
  'orders.nItems': '{count} items',
  'orders.viewDetails': 'View details',
  'orders.editOrder': 'Edit order',
  'orders.cannotEdit':
    'Cannot edit - items already delivered. Unmark items first to edit.',
  'orders.deleteOrder': 'Delete order',
  'orders.showingOrders': 'Showing {from} of {total} orders',
  'orders.pageOfOrders': 'Page {page} of {pages}',
  'orders.matchSearch': '{count} orders match your search',
  'orders.onThisPage': '({rawCount} on this page, {total} total)',
  'orders.clearSearch': 'Clear search to see all {count} orders on this page',

  // Orders — Form Modal
  'orders.createNewOrder': 'Create New Order',
  'orders.editOrderTitle': 'Edit Order',
  'orders.formClient': 'Client',
  'orders.formItems': 'Items *',
  'orders.formTotalAmount': 'Total Amount',
  'orders.addItem': 'Add Item',
  'orders.createOrder': 'Create Order',
  'orders.updateOrder': 'Update Order',
  'orders.walkInCustomer': 'Walk-in Customer',
  'orders.selectItem': 'Select item...',
  'orders.qty': 'Qty',
  'orders.formPrice': 'Price',

  // Orders — Delete Modal
  'orders.deleteOrderTitle': 'Delete Order',
  'orders.deleteConfirm':
    'Are you sure you want to delete this order? This action cannot be undone.',

  // Orders — Confirm Action Modal
  'orders.confirmAction': 'Confirm Action',

  // Orders — Detail Modal
  'orders.orderDetails': 'Order Details',
  'orders.orderItems': 'Order Items',
  'orders.actionsTitle': 'Actions',
  'orders.reactivateOrder': 'Reactivate Order',
  'orders.reopenOrder': 'Reopen Order',
  'orders.statusLabel': 'Status',
  'orders.clientLabel': 'Client',
  'orders.totalAmount': 'Total Amount',
  'orders.createdAt': 'Created At',
  'orders.itemsDeliveredOn': 'Items Delivered On',
  'orders.paymentCompletedOn': 'Payment Completed On',
  'orders.quantityLabel': 'Quantity: {qty}',
  'orders.listedPrice': 'Listed Price: {price}',
  'orders.sellPrice': 'Sell Price: {price}',
  'orders.discountPercent': '({pct}% off)',
  'orders.markupPercent': '(+{pct}% markup)',
  'orders.savedAmount': 'Saved {amount}',
  'orders.itemFallback': 'Item {id}',
  'orders.cannotEditWarning': 'Items cannot be edited',
  'orders.cannotEditDescription':
    'Items have been delivered and removed from inventory. To edit items, you must first unmark them.',
  'orders.markDelivered': 'Mark Items Delivered',
  'orders.removeFromInventory': 'Remove items from inventory',
  'orders.completePayment': 'Complete Payment',
  'orders.markPaymentComplete': 'Mark payment from client as complete',
  'orders.markDeliveredBtn': 'Mark Delivered',
  'orders.unmark': 'Unmark',
  'orders.markPaid': 'Mark Paid',
  'orders.completeOrder': 'Complete Order',
  'orders.cancelOrder': 'Cancel Order',
  'orders.markAsPending': 'Mark as Pending',
  'orders.confirmDelivery':
    'This will remove the items from your inventory. Continue?',
  'orders.confirmUndelivery':
    'This will add the items back to inventory. Continue?',
  'orders.confirmPayment': 'Mark payment as completed?',
  'orders.confirmUnpayment': 'Unmark payment as completed?',
  'orders.confirmComplete': 'Mark this order as completed?',
  'orders.confirmCancel': 'Cancel this order? This action cannot be undone.',
  'orders.reactivateDescription':
    'This will change the status back to pending, allowing you to complete or modify the order.',
  'orders.reopenDescription':
    'This will change the status back to pending. Dates (items delivered, payment completed) will be preserved.',

  // Orders — Selection Bar
  'orders.nOrdersSelected': '{count} order(s) selected',
  'orders.report': 'Report',
  'orders.receipt': 'Receipt',
  'orders.printReceipts': 'Print Receipts',

  // Orders — Print
  'orders.print.storeManager': 'Store Manager',
  'orders.print.total': 'Total:',
  'orders.print.grandTotal': 'GRAND TOTAL:',
  'orders.print.thankYou': 'Thank you!',
  'orders.print.walkIn': 'Walk-in',
  'orders.print.itemFallback': 'Item',
  'orders.print.businessReport': 'Business Report',
  'orders.print.generatedOn': 'Generated on {date}',
  'orders.print.totalOrders': 'Total Orders',
  'orders.print.totalRevenue': 'Total Revenue',
  'orders.print.avgOrder': 'Avg. Order',
  'orders.print.walkInCustomer': 'Walk-in Customer',
  'orders.print.endOfReport': 'End of Report',
  'orders.print.ordersAcross': '{orders} orders across {days} day(s)',
  'orders.print.thankYouMessage': 'Thank you for using Store Manager!',
  'orders.print.receiptTitle': 'Receipt - {date}',
  'orders.print.reportTitle': 'Business Report - {date}',
  'orders.print.item': 'Item',
  'orders.print.qty': 'Qty',
  'orders.print.price': 'Price',
  'orders.print.unitPrice': 'Unit Price',
  'orders.print.subtotal': 'Subtotal',
  'orders.print.orderTotal': 'Order Total:',
  'orders.print.days': 'Days',
  'orders.print.nOrders': '{count} order(s)',

  // ============================================
  // Quick Restock Modal
  // ============================================
  'restock.title': 'Quick Restock',
  'restock.summary': '{items} · {suppliers} · Est. {cost}',
  'restock.nItems': '{count} item',
  'restock.nItemsPlural': '{count} items',
  'restock.nSuppliers': '{count} supplier',
  'restock.nSuppliersPlural': '{count} suppliers',
  'restock.noItems': 'No items to restock',
  'restock.unassigned': 'Unassigned',
  'restock.current': 'Current: {quantity} {unit}',
  'restock.alertAt': 'Alert: {threshold} (user-set)',
  'restock.alertAtTooltip':
    'Low stock threshold configured by you in item settings',
  'restock.removeItem': 'Remove item',
  'restock.supplier': 'Supplier',
  'restock.orderQuantity': 'Order Quantity ({unit})',
  'restock.unitPrice': 'Unit Price',
  'restock.subtotal': 'Subtotal: {amount}',
  'restock.totalCost': 'Total Cost:',
  'restock.createImports': 'Create {count} Import',
  'restock.createImportsPlural': 'Create {count} Imports',
  'restock.creating': 'Creating...',
  'restock.assignSuppliers': 'Please assign suppliers to items',
  'restock.noSuppliersTitle': 'No suppliers found',
  'restock.noSuppliersDescription':
    'You can still create a restock import without a supplier, or create a supplier first for cleaner purchasing records.',
  'restock.noSuppliersOption': 'No suppliers available',
  'restock.createSupplier': 'Create supplier',
  'restock.successSingle': 'Successfully created {count} import order',
  'restock.successPlural': 'Successfully created {count} import orders',
  'restock.aiTooltip': 'AI-powered suggestion based on burn rate & lead time',
  'restock.aiDetected': 'AI detected',
  'restock.aiDetectedTooltip':
    'This item was flagged by AI intelligence based on burn rate and supplier lead time, not just the stock threshold',

  // ============================================
  // Imports
  // ============================================
  'imports.title': 'Imports',
  'imports.subtitle':
    'Manage purchase orders and stock replenishment from suppliers',
  'imports.newImport': 'New Import',
  'imports.restockLowStock': 'Restock Low Stock',
  'imports.allImports': 'All Imports',

  // Imports — Table
  'imports.loadingImports': 'Loading imports...',
  'imports.noMatch': 'No imports match your search',
  'imports.noImports': 'No imports found',
  'imports.importId': 'Import ID',
  'imports.supplier': 'Supplier',
  'imports.nItems': '{count} items',

  // Imports — Filters
  'imports.searchPlaceholder': 'Search by ID, supplier, email, phone...',
  'imports.allSuppliers': 'All Suppliers',
  'imports.sortDate': 'Date',
  'imports.sortTotalPrice': 'Total Price',
  'imports.sortSupplierName': 'Supplier Name',
  'imports.sortNewestFirst': 'Newest First',
  'imports.sortOldestFirst': 'Oldest First',
  'imports.noSupplier': 'No supplier',
  'imports.printMode': 'Print Mode:',
  'imports.a4Report': 'A4 Report',
  'imports.thermalReceipt': 'Thermal Receipt (80mm)',

  // Imports — Form Modal
  'imports.createImport': 'Create Import',
  'imports.editImport': 'Edit Import',
  'imports.supplierOptional': 'Supplier (Optional)',
  'imports.formItems': 'Items *',
  'imports.unitPrice': 'Unit Price',
  'imports.subtotal': 'Subtotal:',
  'imports.formTotal': 'Total:',
  'imports.addItem': '+ Add Item',
  'imports.selectItem': 'Select item...',
  'imports.validationMinItem':
    'Please add at least one item with all fields filled',

  // Imports — Delete Modal
  'imports.deleteImport': 'Delete Import',
  'imports.deleteConfirm':
    'Are you sure you want to delete import {id}? This action cannot be undone.',

  // Imports — Detail
  'imports.confirmAction': 'Confirm Action',
  'imports.supplierLabel': 'Supplier:',
  'imports.statusLabel': 'Status:',
  'imports.totalLabel': 'Total:',
  'imports.itemsReceived': 'Items Received:',
  'imports.paymentCompleted': 'Payment Completed:',
  'imports.createdLabel': 'Created:',
  'imports.actionsTitle': 'Actions',
  'imports.markReceived': 'Mark Items Received',
  'imports.addToInventory': 'Add items to inventory',
  'imports.completePayment': 'Complete Payment',
  'imports.markPaymentToSupplier': 'Mark payment to supplier as complete',
  'imports.reactivateImport': 'Reactivate Import',
  'imports.reactivateDescription':
    'This will change the status back to pending, allowing you to complete or modify the import.',
  'imports.reopenImport': 'Reopen Import',
  'imports.reopenDescription':
    'This will change the status back to pending. Dates (items received, payment completed) will be preserved.',
  'imports.markReceivedBtn': 'Mark Received',
  'imports.unmark': 'Unmark',
  'imports.markPaid': 'Mark Paid',
  'imports.completeImport': 'Complete Import',
  'imports.cancelImport': 'Cancel Import',
  'imports.markAsPending': 'Mark as Pending',
  'imports.confirmReceive':
    'This will add the items to your inventory. Continue?',
  'imports.confirmUnreceive':
    'This will remove the items from inventory. Continue?',
  'imports.confirmPayment': 'Mark payment as completed?',
  'imports.confirmUnpayment': 'Unmark payment as completed?',
  'imports.confirmComplete': 'Mark this import as completed?',
  'imports.confirmCancel': 'Cancel this import? This action cannot be undone.',
  'imports.cannotEditWarning': 'Items cannot be edited',
  'imports.cannotEditDescription':
    'Items have been received and are in inventory. To edit items, you must first unmark them.',
  'imports.cannotEditTooltip':
    'Cannot edit - items already in inventory. Unmark items first to edit.',

  // Imports — Pagination
  'imports.showingImports': 'Showing {from} of {total} imports',
  'imports.pageOfImports': 'Page {page} of {pages}',
  'imports.matchSearch': '{count} imports match your search',
  'imports.clearSearch': 'Clear search to see all {count} imports on this page',

  // Imports — Selection & Print
  'imports.nSelected': '{count} import(s) selected',
  'imports.printSelected': 'Print Selected',
  'imports.print.purchaseImports': 'PURCHASE IMPORTS',
  'imports.print.purchaseImportReport': 'PURCHASE IMPORT REPORT',
  'imports.print.purchaseReport': 'Purchase Report',
  'imports.print.totalImports': 'Total Imports',
  'imports.print.totalValue': 'Total Value',
  'imports.print.avgImport': 'Average Import',
  'imports.print.generatedOn': 'Generated on {date}',
  'imports.print.noSupplier': 'No Supplier',
  'imports.print.grandTotal': 'GRAND TOTAL:',
  'imports.print.storeManager': 'Store Manager',
  'imports.print.receiptTitle': 'Import Receipt - {date}',
  'imports.print.reportTitle': 'Import Report - {date}',
  'imports.print.nImports': '{count} import(s)',
  'imports.print.total': 'Total:',
  'imports.print.days': 'Days',
  'imports.print.item': 'Item',
  'imports.print.id': 'ID',
  'imports.print.supplier': 'Supplier',
  'imports.print.items': 'Items',
  'imports.print.status': 'Status',
  'imports.print.importsAndTotal': '{count} imports - {total}',
  'imports.print.endOfReport': 'End of Report',
  'imports.print.thankYouMessage': 'Thank you for using Store Manager!',

  // Imports — Tooltips
  'imports.viewDetails': 'View details',
  'imports.editImportTooltip': 'Edit import',
  'imports.deleteImportTooltip': 'Delete import',

  // ============================================
  // Transfers
  // ============================================
  'transfers.title': 'Transfers',
  'transfers.subtitle': 'Track stock movements between storehouses',
  'transfers.featureGate':
    'Inter-storehouse transfers require a Pro or Enterprise plan. Upgrade to move stock between locations.',
  'transfers.noTransfers': 'No transfers found',
  'transfers.searchPlaceholder': 'Search by ID...',
  'transfers.emptyDescription':
    'Transfers will appear here when you move stock between storehouses.',
  'transfers.emptyHint':
    'Go to Inventory → View Item → Transfer to move stock.',
  'transfers.exceptionsTitle': 'Transfer Exceptions',
  'transfers.exceptionsSubtitle':
    'Prioritize transfers that are stuck in transit or partially received for too long.',
  'transfers.overdueInTransit': 'Overdue In Transit',
  'transfers.stalledPartialReceives': 'Stalled Partial Receipts',
  'transfers.noPendingExceptions':
    'No transfer exceptions need attention right now.',
  'transfers.noOverdueInTransit': 'No overdue in-transit transfers.',
  'transfers.noStalledPartialReceives':
    'No stalled partially received transfers.',
  'transfers.exceptionReferenceDate': 'Needs review since {date}',
  'transfers.viewDetails': 'View Details',
  'transfers.activityTimeline': 'Activity Timeline',
  'transfers.timelineCreated': 'Transfer created',
  'transfers.timelineShipped': 'Transfer shipped',
  'transfers.timelineInTransit': 'Marked in transit',
  'transfers.timelineReceived': 'Received {quantity} {unit}',
  'transfers.timelineCompleted': 'Transfer completed',
  'transfers.timelineDiscrepancy': 'Discrepancy: {reason}',
  'transfers.timelineNoteValue': 'Note: {note}',

  // Transfers — Detail Modal
  'transfers.detailTitle': 'Transfer Details',
  'transfers.itemLabel': 'Item',
  'transfers.quantityLabel': 'Quantity:',
  'transfers.from': 'From',
  'transfers.to': 'To',
  'transfers.note': 'Note',
  'transfers.transferred': 'Transferred',
  'transfers.created': 'Created',
  'transfers.complete': 'Complete',
  'transfers.ship': 'Ship',
  'transfers.markInTransit': 'Mark In Transit',
  'transfers.receive': 'Receive',
  'transfers.statusShipped': 'Shipped',
  'transfers.statusInTransit': 'In Transit',
  'transfers.statusPartiallyReceived': 'Partially Received',
  'transfers.cancelTransfer': 'Cancel Transfer',
  'transfers.completeTransfer': 'Complete Transfer',
  'transfers.deleteTransfer': 'Delete Transfer',
  'transfers.none': 'None',
  'transfers.unknownItem': 'Unknown Item',
  'transfers.units': 'units',

  // Transfers — Confirm
  'transfers.confirmComplete': 'Complete Transfer?',
  'transfers.confirmShip': 'Ship Transfer?',
  'transfers.confirmMarkInTransit': 'Mark Transfer In Transit?',
  'transfers.confirmReceive': 'Receive Transfer?',
  'transfers.confirmCancel': 'Cancel Transfer?',
  'transfers.confirmDelete': 'Delete Transfer?',
  'transfers.confirmCompleteDesc':
    'This will move {quantity} {unit} of "{itemName}" from {from} to {to}.',
  'transfers.confirmShipDesc':
    'This marks the transfer as shipped and ready for transit.',
  'transfers.confirmMarkInTransitDesc':
    'This marks the transfer as currently in transit.',
  'transfers.confirmReceiveDesc':
    'Receive quantity now. You can partially receive and complete later.',
  'transfers.confirmCancelDesc':
    'This will cancel the pending transfer. No stock will be moved.',
  'transfers.confirmDeleteDesc':
    'This will permanently delete this transfer record.',
  'transfers.receiveQuantity': 'Receive Quantity',
  'transfers.receiveRemaining': 'Remaining to receive: {qty}',
  'transfers.availableNow': 'Available now in source: {qty}',
  'transfers.availableLessThanRemaining':
    'Current source stock is lower than remaining transfer quantity. Adjusted to the maximum receivable now.',
  'transfers.noAvailableToReceive':
    'No stock is currently available to receive for this transfer.',
  'transfers.discrepancyReasonOptional': 'Discrepancy Reason (Optional)',
  'transfers.receiveNoteOptional': 'Receive Note (Optional)',
  'transfers.reasonDamaged': 'Damaged',
  'transfers.reasonMissing': 'Missing',
  'transfers.reasonWrongItem': 'Wrong Item',
  'transfers.reasonQtyMismatch': 'Quantity Mismatch',
  'transfers.reasonOther': 'Other',
  'transfers.deleted': 'Transfer deleted',

  // ============================================
  // Clients
  // ============================================
  'clients.title': 'Clients',
  'clients.subtitle': 'Manage your clients (customers)',
  'clients.newClient': 'New Client',
  'clients.searchPlaceholder': 'Search clients...',
  'clients.loadingClients': 'Loading clients...',
  'clients.noClients': 'No clients found',
  'clients.createPartner': 'Create Client',
  'clients.editPartner': 'Edit Client',
  'clients.deletePartner': 'Delete Client',
  'clients.clientName': 'Client Name *',
  'clients.phoneNumber': 'Phone Number',
  'clients.deleteConfirm':
    'Are you sure you want to delete {name}? This action cannot be undone.',
  'clients.showingClients': 'Showing {from} of {total} clients',
  'clients.pageOfClients': 'Page {page} of {pages}',
  'clients.transactions': '{name} - Transactions',
  'clients.noTransactions': 'No transactions found',
  'clients.noTransactionsMatch': 'No transactions match your filters',
  'clients.showingTransactions': 'Showing {from} of {total} transactions',
  'clients.viewTransactions': 'View transactions',
  'clients.editClient': 'Edit client',
  'clients.deleteClient': 'Delete client',
  'clients.toggleFilters': 'Toggle Filters',
  'clients.printReport': 'Print Report',
  'clients.filters': 'Filters',

  // Clients — Print
  'clients.print.transactionsTitle': 'Client Transactions - {name}',
  'clients.print.totalTransactions': 'Total Transactions',
  'clients.print.totalAmount': 'Total Amount',
  'clients.print.avgTransaction': 'Average Transaction',
  'clients.print.generatedOn': 'Generated on {date}',
  'clients.print.transactionTotal': 'Transaction Total:',
  'clients.print.storeManager': 'Store Manager',
  'clients.print.clientFallback': 'Client',
  'clients.card.transaction': 'Transaction',
  'clients.card.status': 'Status:',
  'clients.card.total': 'Total:',

  // ============================================
  // Suppliers
  // ============================================
  'suppliers.title': 'Suppliers',
  'suppliers.subtitle': 'Manage your suppliers (vendors)',
  'suppliers.newSupplier': 'New Supplier',
  'suppliers.searchPlaceholder': 'Search suppliers...',
  'suppliers.loadingSuppliers': 'Loading suppliers...',
  'suppliers.noSuppliers': 'No suppliers found',
  'suppliers.createSupplier': 'Create Supplier',
  'suppliers.editSupplier': 'Edit Supplier',
  'suppliers.deleteSupplier': 'Delete Supplier',
  'suppliers.supplierName': 'Supplier Name *',
  'suppliers.phoneNumber': 'Phone Number',
  'suppliers.deleteConfirm':
    'Are you sure you want to delete {name}? This action cannot be undone.',
  'suppliers.showingSuppliers': 'Showing {from} of {total} suppliers',
  'suppliers.pageOfSuppliers': 'Page {page} of {pages}',
  'suppliers.imports': '{name} - Imports',
  'suppliers.noImports': 'No imports found',
  'suppliers.noImportsMatch': 'No imports match filters',
  'suppliers.showingImports': 'Showing {from} of {total} imports',
  'suppliers.viewImports': 'View imports',
  'suppliers.editSupplierAction': 'Edit supplier',
  'suppliers.deleteSupplierAction': 'Delete supplier',
  'suppliers.toggleFilters': 'Toggle filters',
  'suppliers.printImports': 'Print imports',

  // Suppliers — Print
  'suppliers.print.importsTitle': 'Supplier Imports - {name}',
  'suppliers.print.totalImports': 'Total Imports',
  'suppliers.print.totalAmount': 'Total Amount',
  'suppliers.print.avgImport': 'Average Import',
  'suppliers.print.generatedOn': 'Generated on {date}',
  'suppliers.print.importTotal': 'Import Total:',
  'suppliers.print.storeManager': 'Store Manager',
  'suppliers.print.supplierFallback': 'Supplier',
  'suppliers.card.import': 'Import',
  'suppliers.card.status': 'Status:',
  'suppliers.card.total': 'Total:',

  // ============================================
  // Floating Action Button
  // ============================================
  'fab.createOrder': 'Create Order',
  'fab.addItem': 'Add Item',
  'fab.addClient': 'Add Client',
  'fab.addSupplier': 'Add Supplier',

  // ============================================
  // Settings
  // ============================================
  'settings.title': 'Business Settings',
  'settings.subtitle':
    'Manage your business information, regional preferences, and storehouses',
  'settings.businessInfo': 'Business Information',
  'settings.businessInfoDesc': 'General details about your business',
  'settings.regionalSettings': 'Regional Settings',
  'settings.regionalSettingsDesc': 'Currency and timezone for your business',
  'settings.storehouses': 'Storehouses',
  'settings.storehousesDesc': 'Manage your warehouse locations',
  'settings.newStorehouse': 'New Storehouse',
  'settings.businessName': 'Business Name',
  'settings.currency': 'Currency',
  'settings.timezone': 'Timezone',
  'settings.storehouseName': 'Name *',
  'settings.storehouseAddress': 'Address *',
  'settings.storehousePhone': 'Phone',
  'settings.storehouseEmail': 'Email',
  'settings.currencyHelp': 'All prices will display in this currency.',
  'settings.timezoneHelp': 'All timestamps will display in this timezone.',
  'settings.saveChanges': 'Save Changes',
  'settings.addStorehouse': '+ Add Storehouse',
  'settings.creating': 'Creating...',
  'settings.locked': 'Locked',
  'settings.noBusinessFound':
    'No business found. Please contact your administrator.',
  'settings.noStorehouses': 'No storehouses yet. Add one to get started.',
  'settings.deleteStorehouse': 'Delete Storehouse?',
  'settings.deleteStorehouseConfirm':
    'Are you sure you want to delete "{name}"? Items assigned to this storehouse may be affected.',
  'settings.businessSaved': 'Business settings saved',
  'settings.storehouseCreated': 'Storehouse created',
  'settings.storehouseUpdated': 'Storehouse updated',
  'settings.storehouseDeleted': '"{name}" deleted',

  // Settings — Placeholders
  'settings.businessNamePlaceholder': 'Your Business Name',
  'settings.businessEmailPlaceholder': 'business@example.com',
  'settings.businessPhonePlaceholder': '+1 234 567 890',
  'settings.businessAddressPlaceholder': '123 Main St, City',
  'settings.storehouseNamePlaceholder': 'Main Warehouse',
  'settings.storehouseAddressPlaceholder': '123 Storage Rd',
  'settings.storehousePhonePlaceholder': '+1 234 567 890',
  'settings.storehouseEmailPlaceholder': 'warehouse@example.com',

  // Settings — Danger Zone
  'settings.dangerZone': 'Danger Zone',
  'settings.dangerZoneDesc':
    'Permanent and irreversible operations. Proceed with extreme caution.',
  'settings.deleteBusiness': 'Delete Business',
  'settings.deleteBusinessDesc':
    'Permanently delete this business and all its data — inventory, transactions, employees, and all related records. This cannot be undone.',
  'settings.deleteBusinessButton': 'Delete Business',
  'settings.deleteBusinessConfirmTitle': 'Delete Business?',
  'settings.deleteBusinessConfirmText':
    'This will permanently delete your business and all associated data including employees, inventory, transactions, and reports. All team members will lose access immediately. This action cannot be reversed.',
  'settings.deleteBusinessDeleting': 'Deleting...',
  'settings.deleteBusinessDeleted': 'Business deleted successfully',

  // ============================================
  // Team Management
  // ============================================
  'team.title': 'Team Management',
  'team.subtitle':
    'Manage team members, define custom roles, and assign permissions',
  'team.members': 'Members',
  'team.roles': 'Roles',
  'team.access': 'Access',
  'team.customRolesGate':
    'Custom roles require a Pro or Enterprise plan. Upgrade to create custom permission sets for your team.',
  'team.accessGate':
    'Role & storehouse access assignments require a Pro or Enterprise plan.',

  // Team — Members
  'team.membersTitle': 'Members',
  'team.membersDesc': 'Manage your team members and their account status',
  'team.inviteMember': 'Invite Member',
  'team.inviteTeamMember': 'Invite Team Member',
  'team.email': 'Email',
  'team.emailPlaceholder': 'colleague@example.com',
  'team.check': 'Check',
  'team.checking': 'Checking...',
  'team.emailAvailable': 'Email is available',
  'team.alreadyRegistered': 'Already registered as a user',
  'team.alreadyInvited': 'Already has a pending invitation',
  'team.couldNotVerify': 'Could not verify email',
  'team.makeAdmin': 'Make Admin',
  'team.adminCanManage': 'Admins can manage team members, roles, and settings',
  'team.storehouseAccessLabel': 'Storehouse Access',
  'team.loadingStorehouses': 'Loading storehouses...',
  'team.noStorehousesFound': 'No storehouses found',
  'team.assignRoles': 'Assign Roles',
  'team.loadingRoles': 'Loading roles...',
  'team.noCustomRolesYet':
    'No custom roles defined yet. You can assign them later.',
  'team.sendInvitation': 'Send Invitation',
  'team.sending': 'Sending...',
  'team.invitationSentTo': 'Invitation sent to {email}.',
  'team.invitationSent': 'Invitation Sent',
  'team.invitationFailed': 'Invitation Failed',
  'team.invitationFailedMsg': 'Failed to send invitation. Please try again.',
  'team.searchMembers': 'Search by name or email...',
  'team.allStatus': 'All Status',
  'team.deactivated': 'Deactivated',
  'team.invited': 'Invited',
  'team.allRoles': 'All Roles',
  'team.noMembers': 'No team members found.',
  'team.noMembersMatch': 'No members match your filters.',
  'team.admin': 'Admin',
  'team.owner': 'Owner',
  'team.you': '(you)',
  'team.reactivate': 'Reactivate',
  'team.deactivate': 'Deactivate',
  'team.expires': 'Expires {date}',
  'team.resend': 'Resend',
  'team.resending': 'Sending...',
  'team.deactivateMember': 'Deactivate Member',
  'team.reactivateMember': 'Reactivate Member',
  'team.deactivateConfirm':
    "Are you sure you want to deactivate {name}? They will be logged out immediately and won't be able to access the system until reactivated.",
  'team.reactivateConfirm':
    'Reactivate {name}? They will be able to log in again.',
  'team.userDeactivated': '{name} has been deactivated.',
  'team.userDeactivatedTitle': 'User Deactivated',
  'team.userReactivated': '{name} has been reactivated.',
  'team.userReactivatedTitle': 'User Reactivated',
  'team.invitationResent': 'Invitation resent to {email}',
  'team.invitationResentTitle': 'Invitation Resent',
  'team.accessUpdated': 'User access updated',
  'team.promoted': '{name} promoted to admin',
  'team.demoted': '{name} demoted to user',

  // Team — Access
  'team.accessControl': 'Access Control',
  'team.accessDesc':
    'Assign roles, storehouses, and promote/demote team members',
  'team.searchMembersAccess': 'Search members...',
  'team.noUsers': 'No users found.',
  'team.noUsersMatch': 'No members match your search.',
  'team.fullAccess': 'Full access (admin privileges)',
  'team.rolesLabel': 'Roles: ',
  'team.none': 'None',
  'team.storehousesLabel': 'Storehouses: ',
  'team.noneAssigned': 'None assigned',
  'team.demoteToUser': 'Demote to user',
  'team.promoteToAdmin': 'Promote to admin',
  'team.demoteBtn': 'Demote',
  'team.promoteBtn': 'Promote',
  'team.editAccess': 'Edit access',
  'team.assignedRoles': 'Assigned Roles',
  'team.noRolesDefined': 'No roles defined yet',
  'team.storehouseAccess': 'Storehouse Access',
  'team.leaveEmptyDeny': 'Leave empty to deny all storehouse access.',
  'team.noStorehouses': 'No storehouses found',
  'team.saveChanges': 'Save Changes',

  // ============================================
  // Roles (RoleManager)
  // ============================================
  'role.customRoles': 'Custom Roles',
  'role.customRolesDesc':
    'Define permission sets that can be assigned to users',
  'role.newRole': '+ New Role',
  'role.roleName': 'Role Name *',
  'role.roleNamePlaceholder': 'e.g. Warehouse Staff',
  'role.roleDescription': 'Description',
  'role.roleDescriptionPlaceholder': 'What this role is for',
  'role.permissionsLabel': 'Permissions',
  'role.readAccessNote':
    'Read access is granted to all users automatically. Toggle write permissions below.',
  'role.toggleAll': 'Toggle all',
  'role.saving': 'Saving...',
  'role.createRole': 'Create Role',
  'role.saveChanges': 'Save Changes',
  'role.noRolesYet':
    'No custom roles yet. Create one to assign permissions to users.',
  'role.permissionCount': '{count} permission{s}',
  'role.deleteRoleTitle': 'Delete Role?',
  'role.deleteRoleConfirm':
    'Are you sure you want to delete "{name}"? Users assigned this role will lose these permissions.',
  'role.roleCreated': 'Role created',
  'role.roleUpdated': 'Role updated',
  'role.roleDeleted': '"{name}" deleted',
  'role.resource_items': 'Inventory Items',
  'role.resource_imports': 'Imports (Purchases)',
  'role.resource_transactions': 'Orders (Sales)',
  'role.resource_transfers': 'Transfers',
  'role.resource_partners': 'Partners',
  'role.resource_storehouses': 'Storehouses',
  'role.resource_businessSettings': 'Business Settings',
  'role.resource_users': 'Users',
  'role.action_create': 'Create',
  'role.action_update': 'Update',
  'role.action_delete': 'Delete',
  'role.action_execute': 'Execute',
  'role.actionDesc_create': 'Create new records',
  'role.actionDesc_update': 'Edit existing records',
  'role.actionDesc_delete': 'Remove records',
  'role.actionDesc_execute':
    'Perform status changes (approve, complete, cancel, assign)',

  // ============================================
  // Billing
  // ============================================
  'billing.title': 'Billing & Subscription',
  'billing.subtitle': 'Manage your plan, view usage, and unlock more features.',

  // Billing — Dimension & Feature Labels
  'billing.storehouses': 'Storehouses',
  'billing.teamMembers': 'Team Members',
  'billing.itemsLabel': 'Items',
  'billing.monthlyTransactions': 'Monthly Transactions',
  'billing.featureTransfers': 'Inter-Storehouse Transfers',
  'billing.featureCustomRoles': 'Custom Roles & Permissions',
  'billing.featureBasicAnalytics': 'Basic Revenue Analytics',
  'billing.featureAnalyticsReports': 'Analytics & Reports',
  'billing.featureAdvancedReports': 'Advanced Intelligence & Insights',
  'billing.featureBarcodePrint': 'Barcode & QR Label Printing',

  // Billing — Trial
  'billing.tryProFree': 'Try Pro for free!',
  'billing.trialDescription':
    ' Start a 30-day Pro trial — no credit card required.',
  'billing.starting': 'Starting…',
  'billing.startFreeTrial': 'Start Free Trial',
  'billing.confirmTrialTitle': 'Start Pro Trial?',
  'billing.confirmTrialBody':
    'This will activate a 30-day Pro trial for your business. No credit card is required. You can only use the free trial once.',
  'billing.proTrialActive': 'Pro Trial Active',
  'billing.trialCountdown':
    ' — {days} day(s) remaining (ends {date}). Upgrade to keep your Pro features.',
  'billing.upgradeNow': 'Upgrade Now',
  'billing.trialStarted': 'Your 30-day Pro trial has started!',

  // Billing — Past Due
  'billing.paymentFailed': 'Payment failed',
  'billing.paymentFailedDesc':
    " — Your last payment didn't go through. Please update your payment method to keep your subscription active.",
  'billing.opening': 'Opening…',
  'billing.fixPayment': 'Fix Payment',

  // Billing — Pending Downgrade
  'billing.switchingTo': 'Switching to {plan} at next renewal',
  'billing.switchingToDesc':
    ' — takes effect on {date}. Your current plan remains active until then.',
  'billing.cancelling': 'Cancelling…',
  'billing.keepCurrentPlan': 'Keep Current Plan',

  // Billing — Manual Payment Request
  'billing.requestPending': 'Payment request pending',
  'billing.requestPendingDesc':
    'Your request for {plan} ({cycle}) is awaiting approval. You will be notified once confirmed.',
  'billing.requestSubmitted': 'Payment request submitted. Awaiting approval.',
  'billing.cancelRequest': 'Cancel Request',
  'billing.requestCancelled': 'Payment request cancelled.',
  'billing.requestAlreadyPending':
    'A payment request is already pending. Cancel it first to select a different plan.',
  'billing.manualDowngradeBlocked':
    'Downgrades are not available during the current billing period. Your plan runs until {date}. After it ends, you can renew with a different plan.',

  // Billing — Add-On Request
  'billing.requestAddOn': 'Request Add-Ons',
  'billing.requestingAddOn': 'Requesting…',
  'billing.addonRequestPending': 'Add-on request pending',
  'billing.addonRequestPendingDesc':
    'Your add-on request is under review. We will contact you soon.',
  'billing.addonSubmitted':
    'Add-on request submitted. Our team will review it shortly.',
  'billing.customLimitsAvailable': 'Custom limits available',
  'billing.addonPlaceholder':
    'Tell us about your needs — expected usage, team size, etc.',
  'billing.requestedLimits': 'Requested Limits',
  'billing.addonMessage': 'Message (optional)',
  'billing.addonBasePrice': 'Plan base',
  'billing.addonAddOn': 'Add-on',
  'billing.addonBaseIncluded': '{count} included',
  'billing.addonExtraUnits': '+{extra} extra',
  'billing.addonMin': 'Min {value}',
  'billing.addonPerUnit': '{price} per {step}',
  'billing.addonEstimatedMonthly': 'Estimated monthly',
  'billing.addonEstimatedAnnual': 'Estimated annual (20% off)',
  'billing.addonYourLimits': 'Customize Your Limits',
  'billing.addonPriceSummary': 'Price Estimate',
  'billing.addonNoAddOns': 'Base plan limits — no add-ons',

  // Billing — Add-Ons Section
  'billing.addOnsTitle': 'Add-Ons',
  'billing.addOnsDesc':
    'Expand your limits beyond the base plan with additional capacity.',
  'billing.requestAddOns': 'Request Add-Ons',

  // Billing — Current Plan
  'billing.currentPlan': 'Current Plan',
  'billing.onPlan': 'You are on the {plan} plan',
  'billing.status_active': 'Active',
  'billing.status_trialing': 'Trial',
  'billing.status_past_due': 'Past Due',
  'billing.status_canceled': 'Canceled',
  'billing.status_expired': 'Expired',
  'billing.annual': 'Annual',
  'billing.monthly': 'Monthly',
  'billing.unableToLoad': 'Unable to load subscription data. Please try again.',
  'billing.nextRenewal': 'Next renewal: ',
  'billing.cycle': 'Cycle: ',
  'billing.cancelsOn': 'Cancels on: ',
  'billing.reactivating': 'Reactivating…',
  'billing.undoCancel': 'Undo Cancel',
  'billing.card': 'Card: ',
  'billing.nextInvoice': 'Next invoice: ',
  'billing.featureAccess': 'Feature Access',

  // Billing — Plan Comparison
  'billing.availablePlans': 'Available Plans',
  'billing.save20': 'Save 20%',
  'billing.comingSoon': 'Coming Soon',
  'billing.limits': 'Limits',
  'billing.features': 'Features',
  'billing.currentPlanBtn': 'Current Plan',
  'billing.changeScheduled': 'Change Scheduled',
  'billing.pendingChangeLock': 'Pending change in progress',
  'billing.changing': 'Changing...',
  'billing.switchTo': 'Switch to {plan}',
  'billing.adminRequired': 'Admin Required',
  'billing.cancelToDowngrade': 'Use "Cancel Plan" to downgrade',
  'billing.cannotSwitchAnnualMonthly':
    'Cannot switch from annual to monthly billing',
  'billing.cannotUpgradeAnnualToMonthly':
    'Switch to monthly billing first before upgrading',
  'billing.cannotCrossTypeTransfer':
    'Not applicable for transferring between these plans',

  // Billing — Management
  'billing.billingManagement': 'Billing Management',
  'billing.billingManagementDesc':
    'Manage payment methods, download invoices, or cancel your subscription.',
  'billing.manageBilling': 'Manage Billing',
  'billing.cancelPlan': 'Cancel Plan',

  // Billing — Invoice History
  'billing.invoiceHistory': 'Invoice History',
  'billing.amount': 'Amount',
  'billing.invoice': 'Invoice',
  'billing.view': 'View',

  // Billing — Subscription History
  'billing.subscriptionHistory': 'Subscription History',
  'billing.event.plan_activated': 'Plan Activated',
  'billing.event.addon_activated': 'Add-on Activated',
  'billing.event.plan_subscribed': 'Subscribed',
  'billing.event.plan_upgraded': 'Plan Upgraded',
  'billing.event.plan_renewed': 'Plan Renewed',
  'billing.event.trial_started': 'Trial Started',
  'billing.event.trial_expired': 'Trial Expired',
  'billing.event.plan_canceled': 'Plan Canceled',
  'billing.event.plan_reactivated': 'Plan Reactivated',
  'billing.event.downgrade_scheduled': 'Downgrade Scheduled',
  'billing.event.downgrade_executed': 'Downgrade Executed',
  'billing.event.downgrade_canceled': 'Downgrade Canceled',
  'billing.eventPlan': 'Plan',
  'billing.eventPreviousPlan': 'Previous',
  'billing.eventCycle': 'Cycle',
  'billing.noSubscriptionHistory': 'No subscription history yet.',

  // Billing — Locked Resources
  'billing.manageLockedResources': 'Manage Locked Resources',
  'billing.manageLockedResourcesDesc':
    'Unlock resources after upgrading, or swap which are locked without changing your plan.',
  'billing.unlockResources': 'Unlock Resources',
  'billing.swap': 'Swap',
  'billing.unlockDesc':
    "Unlock: After upgrading your plan, unlock locked storehouses or reactivate deactivated users (within your new plan's limits).",
  'billing.swapDesc':
    'Swap: Exchange which resources are locked — 1-for-1, net-zero. Limited to 2 swaps per day.',

  // Billing — Confirm Dialogs
  'billing.upgradeToTitle': 'Upgrade to {target}',
  'billing.confirmUpgrade': 'Confirm Upgrade',
  'billing.upgradeDescription':
    'Your plan will be upgraded immediately to {target} at {price} ({cycle}).',
  'billing.upgradeProration':
    'Your payment method on file will be charged the prorated difference for the remainder of this billing period.',
  'billing.upgradeNextCycle':
    "Starting next billing cycle you'll be charged the full {target} rate.",
  'billing.switchToTitle': 'Switch to {tier}',
  'billing.scheduleChange': 'Schedule Change',
  'billing.switchDescription':
    'Your current plan will remain active until the end of your billing period. The plan change will take effect at the next renewal.',
  'billing.switchNoCharge':
    "No immediate charge — you'll continue to have full access to your current plan's features until then.",
  'billing.cancelScheduledTitle': 'Cancel Scheduled Plan Change',
  'billing.cancelScheduledConfirm':
    "Cancel the scheduled plan change? Your current plan will continue at the next renewal and you'll be charged the current rate.",
  'billing.cancelSubscriptionTitle': 'Cancel Subscription',
  'billing.cancelSubscriptionConfirm':
    'Are you sure you want to cancel your subscription?',
  'billing.cancelSubscriptionDesc':
    'Your plan will remain active until the end of the current billing period. After that, your account will be downgraded to the Free plan.',

  // Billing — Notifications
  'billing.subscriptionUpdated':
    'Subscription updated! Your plan will activate shortly.',
  'billing.alreadyProcessed':
    'Your payment was already processed. Your plan will activate automatically — please wait a moment and refresh.',
  'billing.planChangeScheduled':
    'Plan change to {tier} ({cycle}) scheduled. It will take effect at the next renewal.',
  'billing.scheduleCancelled':
    'Scheduled plan change cancelled. Your current plan will continue.',
  'billing.subscriptionCancelled':
    'Subscription will be cancelled at the end of the billing period.',
  'billing.planChanged': 'Plan changed to {target}',
  'billing.resourcesUnlocked': 'Resources unlocked successfully',
  'billing.resourcesSwapped': 'Resources swapped successfully',
  'billing.reactivated':
    'Subscription reactivated! It will continue at the next renewal.',
  'billing.changeAlreadyScheduled':
    'A plan change is already scheduled for renewal. Cancel it first to schedule a different one.',
  'billing.cannotSwitchMsg':
    'Cannot switch from an annual plan to a monthly plan. Your annual subscription will auto-renew at the end of the current period.',

  // ============================================
  // Payment Modal
  // ============================================
  'payment.subscribeTo': 'Subscribe to {plan}',
  'payment.billingCycle': '{cycle} billing — {price}',
  'payment.orderSummary': 'Order Summary',
  'payment.plan': 'Plan',
  'payment.billingPeriod': 'Billing period',
  'payment.totalDue': 'Total due',
  'payment.confirmAndPay': 'Confirm & Pay',
  'payment.proceedNote':
    "You'll be redirected to complete your purchase securely.",
  'payment.proceedNoteManual':
    'Submit a request and an admin will contact you to arrange payment.',
  'payment.submitRequest': 'Submit Request',
  'payment.manualNote':
    'Your request will be reviewed by our team. Your account rep will contact you to arrange payment.',
  'payment.submitting': 'Submitting request…',
  'payment.initFailed':
    'Payment provider failed to initialise. Please check your configuration.',
  'payment.formLoadFailed': 'Payment form failed to load. Please try again.',
  'payment.alreadyProcessed':
    'Your payment was already processed! Your plan will activate automatically once the payment is confirmed. Please wait a moment and refresh the page.',
  'payment.formNotReady':
    'Payment form is not ready yet. Please wait for it to load.',
  'payment.paymentFailed': 'Payment failed. Please try again.',
  'payment.unexpectedError': 'An unexpected error occurred. Please try again.',
  'payment.payAmount': 'Pay {price}',
  'payment.securePayments': 'Payments are processed securely by ',
  'payment.provider': 'Xendit',
  'payment.noTouchServers': '. Your card details never touch our servers.',
  'payment.redirecting': 'Redirecting to payment page…',

  // ============================================
  // Resolve Modal
  // ============================================
  'resolve.title': 'Unlock Resources',
  'resolve.description':
    'Select locked storehouses to unlock and deactivated users to reactivate. Your current plan limits will be enforced.',
  'resolve.loadFailed': 'Failed to load resources. Please try again.',
  'resolve.emptyState': 'No locked storehouses or deactivated users to unlock.',
  'resolve.lockedStorehouses': 'Locked Storehouses',
  'resolve.nSelected': '{n} selected',
  'resolve.unlockDesc':
    "Unlocked storehouses will become fully operational again. You can only unlock up to your plan's storehouse limit.",
  'resolve.locked': 'Locked',
  'resolve.deactivatedUsers': 'Deactivated Users',
  'resolve.reactivateDesc':
    "Reactivated users will be able to sign in again. You can only reactivate up to your plan's user limit.",
  'resolve.deactivatedBadge': 'Deactivated',
  'resolve.unlocking': 'Unlocking...',
  'resolve.unlockSelected': 'Unlock Selected',

  // ============================================
  // Compliance Gate
  // ============================================
  'compliance.loading': 'Loading…',
  'compliance.planLimitsExceeded': 'Plan Limits Exceeded',
  'compliance.limitsExceededDesc': 'Your {tier} plan limits have been exceeded',
  'compliance.over': '({excess} over)',
  'compliance.itemsTransactionsHint':
    'Items and transactions cannot be locked. Please upgrade your plan or remove excess resources manually.',
  'compliance.upgradePlan': 'Upgrade Plan',
  'compliance.loadFailed':
    'Failed to load requirements. Please refresh the page.',
  'compliance.selectToComply':
    'Select resources to lock or deactivate to comply with your plan, or upgrade to a higher plan.',
  'compliance.selectStorehousesToLock': 'Select storehouses to lock',
  'compliance.nOfTotalSelected': '{n} / {total} selected',
  'compliance.lockedReadOnly':
    'Locked storehouses become read-only. No items, transactions, or transfers can be created or modified within them.',
  'compliance.selectUsersToDeactivate': 'Select users to deactivate',
  'compliance.deactivateDesc':
    'Deactivated users will be logged out and cannot sign in until reactivated.',
  'compliance.enforceFailed': 'Failed to enforce limits. Please try again.',
  'compliance.backToStorehouses': '← Back to storehouses',
  'compliance.applying': 'Applying...',
  'compliance.nextStep': 'Next →',
  'compliance.confirmContinue': 'Confirm & Continue',
  'compliance.nonAdminMessage': 'Your business has exceeded its plan limits.',
  'compliance.contactAdmin':
    'Please contact your administrator to resolve this issue.',
  'compliance.created': 'Created ',

  // Compliance — Dimension Labels
  'compliance.dimStorehouses': 'Storehouses',
  'compliance.dimUsers': 'Users',
  'compliance.dimItems': 'Items',
  'compliance.dimMonthlyTransactions': 'Monthly Transactions',

  // Compliance — Tier Labels
  'tier.free': 'Free',
  'tier.pro': 'Pro',
  'tier.enterprise': 'Enterprise',
  'tier.freeDesc': 'For small businesses getting started',
  'tier.proDesc': 'For growing businesses with multiple locations',
  'tier.enterpriseDesc': 'For large organizations — custom limits available',
  'tier.pricePerMonth': '{price}/mo',
  'tier.pricePerYear': '{price}/yr',

  // ============================================
  // Analytics
  // ============================================
  'analytics.title': 'Analytics',
  'analytics.subtitle':
    'Financial insights and performance metrics for your business.',
  'analytics.featureGate':
    'Analytics & Reports is available on the Pro plan and above. Upgrade to unlock revenue insights, inventory reports, and more.',
  'analytics.proGate':
    'Upgrade to Pro to unlock trends, top items, partner analytics, inventory reports, and more.',
  'analytics.intelligenceGate':
    'Advanced Intelligence is available on the Enterprise plan. Upgrade to unlock risk scoring, margin analysis, and actionable insights.',
  'analytics.allStorehouses': 'All Storehouses',

  // Analytics — Period options
  'analytics.period7d': 'Last 7 days',
  'analytics.period30d': 'Last 30 days',
  'analytics.period90d': 'Last 90 days',
  'analytics.periodYtd': 'Year to date',
  'analytics.period12m': 'Last 12 months',
  'analytics.periodAll': 'All time',

  // Analytics — Type options
  'analytics.mostSold': 'Most Sold',
  'analytics.mostPurchased': 'Most Purchased',
  'analytics.mostProfitable': 'Most Profitable',
  'analytics.topClients': 'Top Clients',
  'analytics.topSuppliers': 'Top Suppliers',

  // Analytics — Column / label i18n
  'analytics.itemCol': 'Item',
  'analytics.itemsCol': 'Items',
  'analytics.from': 'From',
  'analytics.to': 'To',
  'analytics.ordersLabel': 'Orders:',
  'analytics.importsLabel': 'Imports:',
  'analytics.txCount': 'Orders',
  'analytics.impCount': 'Imports',
  'analytics.liveDataNotice': 'Live data — not filtered by period',
  'analytics.gainTrend': 'Margin Trend',
  'analytics.topTransferredItems': 'Top Transferred Items',
  'analytics.transferCount': 'Transfers',

  // Analytics — Profit & Loss
  'analytics.profitLoss': 'Profit & Loss',
  'analytics.revenue': 'Revenue',
  'analytics.cost': 'Cost',
  'analytics.grossProfit': 'Gross Profit',
  'analytics.activity': 'Activity',
  'analytics.nOrders': '{n} orders',
  'analytics.nImports': '{n} imports',
  'analytics.vsPrevPeriod': 'vs prev period',
  'analytics.margin': '% margin',

  // Analytics — Trends
  'analytics.revenueCostTrend': 'Revenue & Cost Trend',
  'analytics.profit': 'Profit',
  'analytics.visual': 'Visual',
  'analytics.noDataPeriod': 'No data for this period.',

  // Analytics — Top Items
  'analytics.topItems': 'Top Items',
  'analytics.marginCol': 'Margin',
  'analytics.qty': 'Qty',
  'analytics.value': 'Value',
  'analytics.avgPrice': 'Avg Price',
  'analytics.share': 'Share',
  'analytics.noItemsPeriod': 'No items found for this period.',

  // Analytics — Partners
  'analytics.partnerAnalytics': 'Partner Analytics',
  'analytics.totalValue': 'Total Value',
  'analytics.ordersCol': 'Orders',
  'analytics.avgOrder': 'Avg Order',
  'analytics.lastActivity': 'Last Activity',
  'analytics.walkIn': '(Walk-in)',
  'analytics.noPartnerData': 'No partner data found.',

  // Analytics — Inventory Snapshot
  'analytics.inventorySnapshot': 'Inventory Snapshot',
  'analytics.totalValueStat': 'Total Value',
  'analytics.totalItemsStat': 'Total Items',
  'analytics.lowStockStat': 'Low Stock',
  'analytics.deadStock': 'Dead Stock',
  'analytics.turnoverRate': 'Turnover Rate',
  'analytics.itemsBelowThreshold': 'Items below threshold',
  'analytics.noSalesInPeriod': 'No sales in period',
  'analytics.turnoverDesc': 'Units sold / current stock',
  'analytics.byStorehouse': 'By Storehouse',
  'analytics.inventory': 'Inventory',
  'analytics.lockedBadge': '(Locked)',

  // Analytics — Payment & Delivery Insights
  'analytics.paymentDeliveryInsights': 'Payment & Delivery Insights',
  'analytics.outstandingPayments': 'Outstanding Payments',
  'analytics.customerReceivables': 'Customer Receivables',
  'analytics.supplierPayables': 'Supplier Payables',
  'analytics.averageLagDays': 'Average Lag (days)',
  'analytics.paymentLag': 'Payment Lag',
  'analytics.deliveryLag': 'Delivery Lag',

  // Analytics — Margin Analysis
  'analytics.marginAnalysis': 'Margin Analysis',
  'analytics.totalGain': 'Total Gain (Sell − Buy)',
  'analytics.sumAcrossItems': 'sum across all sold items',
  'analytics.avgMargin': 'Avg Margin',
  'analytics.avgMarginDesc': '(sell − avg buy cost) / sell',
  'analytics.itemsTracked': 'Items Tracked',
  'analytics.topItemsByMargin': 'Top Items by Margin',
  'analytics.avgBuy': 'Avg Buy',
  'analytics.avgSell': 'Avg Sell',
  'analytics.marginPercent': 'Margin %',
  'analytics.totalGainCol': 'Total Gain',

  // Analytics — Storehouse Comparison
  'analytics.storehouseComparison': 'Storehouse Comparison',
  'analytics.storehouse': 'Storehouse',
  'analytics.transfersIn': 'Transfers In',
  'analytics.transfersOut': 'Transfers Out',
  'analytics.noStorehouseData': 'No storehouse data available.',

  // Analytics — Transfer Flow
  'analytics.transferFlow': 'Transfer Flow',
  'analytics.flowBetweenStorehouses': 'Flow Between Storehouses',
  'analytics.count': 'Count',
  'analytics.netFlowByStorehouse': 'Net Flow by Storehouse',
  'analytics.in': 'In',
  'analytics.out': 'Out',
  'analytics.net': 'Net',
  'analytics.noTransferData': 'No transfer data for this period.',

  // Analytics — Inventory Risk / Dead Stock
  'analytics.inventoryRisk': 'Inventory Risk',
  'analytics.capitalAtRisk': 'Capital at Risk',
  'analytics.capitalAtRiskCol': 'Capital at Risk',
  'analytics.estCarryingCost': 'Est. Carrying Cost',
  'analytics.forSelectedPeriod': 'For selected period',
  'analytics.criticalItems': 'Critical Items',
  'analytics.highRisk': 'high risk',
  'analytics.avgRiskScore': 'Avg Risk Score',
  'analytics.needsAttention': 'Needs attention',
  'analytics.healthy': 'Healthy',
  'analytics.agingBuckets': 'Aging Buckets',
  'analytics.itemsLabel': 'items',
  'analytics.atRisk': 'at risk',
  'analytics.topRiskItems': 'Top Risk Items',
  'analytics.itemName': 'Item',
  'analytics.riskScore': 'Risk',
  'analytics.daysSinceLastSale': 'Days Since Sale',
  'analytics.sellThrough': 'Sell-Through',
  'analytics.trend': 'Trend',
  'analytics.actions': 'Actions',
  'analytics.expired': 'EXPIRED',
  'analytics.actionTransfer': 'Transfer',
  'analytics.transferDesc': 'Move stock to another storehouse',
  'analytics.actionDiscount': 'Discount',
  'analytics.actionStopReorder': 'Stop reorder',
  'analytics.agingBucketRange': '{min}–{max} days',
  'analytics.agingBucket90Plus': '90+ days',
  'analytics.reason_noTransfers':
    'Item has not been transferred to other locations — consider redistributing stock.',
  'analytics.reason_expiringSoon':
    'Expires soon — consider a clearance discount.',
  'analytics.reason_slowMoving':
    'Slow-moving inventory — consider a promotional discount to accelerate turnover.',
  'analytics.reason_noSales':
    'No sales recorded in the selected period — pause reordering until demand recovers.',

  // View settings
  'analytics.configureViews': 'Configure views',
  'analytics.viewSettings': 'View Settings',
  'analytics.section.profitLoss': 'Profit & Loss',
  'analytics.section.trends': 'Revenue & Cost Trend',
  'analytics.section.topItems': 'Top Items',
  'analytics.section.partners': 'Partner Analytics',
  'analytics.section.inventory': 'Inventory Snapshot',
  'analytics.section.payments': 'Payment & Delivery',
  'analytics.section.storehouses': 'Storehouse Comparison',
  'analytics.section.transfers': 'Transfer Flow',
  'analytics.section.deadStock': 'Inventory Risk / Dead Stock',
  'analytics.section.stockIntelligence': 'Stock Intelligence',
  'analytics.section.margins': 'Margin Analysis',

  // Risk analytics page
  'analytics.backToAnalytics': '← Back to Analytics',
  'analytics.riskAnalytics': 'Inventory Risk Analysis',
  'analytics.riskAnalyticsSubtitle':
    'Full inventory risk assessment with filtering and sorting',
  'analytics.allRiskLevels': 'All risk levels',
  'analytics.riskLevelCritical': 'Critical',
  'analytics.riskLevelHigh': 'High',
  'analytics.riskLevelMedium': 'Medium',
  'analytics.riskLevelLow': 'Low',
  'analytics.searchItems': 'Search items...',

  // Locked section
  'analytics.advancedIntelligence': 'Advanced Intelligence',
  'analytics.unlockIntelligence':
    'Unlock advanced intelligence with an Enterprise plan',

  // Stock Intelligence
  'analytics.stockIntelligence': 'Stock Intelligence',
  'analytics.si.pageTitle': 'Stock Intelligence',
  'analytics.si.pageSubtitle':
    'Predictive stock-out forecasting with financial impact analysis',
  'analytics.si.quickRestock': 'Quick Restock',
  'analytics.si.quickRestockDesc': 'Create an import order for this item',
  'analytics.si.createImport': 'Create Import ({count})',
  'analytics.si.importModalTitle': 'Create Import from Intelligence',
  'analytics.si.importFillQuantities':
    'Please fill in quantities for all items',
  'analytics.si.importQtyPlaceholder': 'Enter qty',
  'analytics.si.importSuccess':
    'Successfully created import with {count} items',
  'analytics.si.revenueAtRisk': 'Revenue at Risk',
  'analytics.si.revenueAtRiskCol': 'Revenue at Risk',
  'analytics.si.criticalItems': 'Critical',
  'analytics.si.urgentItems': 'Urgent Items',
  'analytics.si.urgent': 'urgent',
  'analytics.si.avgDaysLeft': 'Avg Days Left',
  'analytics.si.avgDaysLeftDesc': 'Average stock runway',
  'analytics.si.needReorder': 'Need reorder',
  'analytics.si.pastReorderPoint': 'Past reorder point',
  'analytics.si.topItems': 'Reorder Priority',
  'analytics.si.burnRate': 'Burn Rate',
  'analytics.si.daysLeft': 'Days Left',
  'analytics.si.stockOutDate': 'Stock-Out Date',
  'analytics.si.leadTime': 'Lead Time',
  'analytics.si.supplier': 'Supplier',
  'analytics.si.reorderPoint': 'Reorder Point',
  'analytics.si.urgencyCol': 'Urgency',
  'analytics.si.day': 'day',
  'analytics.si.reliabilityTooltip': 'Supplier delivery reliability score',
  'analytics.si.allUrgencies': 'All urgencies',
  'analytics.si.urgencyCritical': 'Critical',
  'analytics.si.urgencyUrgent': 'Urgent',
  'analytics.si.urgencyWatch': 'Watch',
  'analytics.si.urgencyHealthy': 'Healthy',
  'analytics.si.searchPlaceholder': 'Search items, SKUs, storehouses...',
  'analytics.si.action_reorder-now': 'Reorder now',
  'analytics.si.action_reorder-soon': 'Reorder soon',
  'analytics.si.action_adjust-threshold': 'Adjust threshold',
  'analytics.si.action_monitor': 'Monitor',
  'analytics.si.reason_pastReorderWindow':
    'Stock will run out before the supplier can deliver. Reorder immediately to minimize lost sales.',
  'analytics.si.reason_approachingStockOut':
    'Stock is running low and approaching the reorder window. Order soon to maintain inventory.',
  'analytics.si.reason_thresholdMismatch':
    'Your current low-stock alert threshold is significantly different from the calculated optimal reorder point.',
  'analytics.si.reason_stockHealthy':
    'Stock levels are healthy. No action needed at this time.',

  // ============================================
  // Profile
  // ============================================
  'profile.title': 'Profile',
  'profile.subtitle': 'View and manage your account information',
  'profile.loadingTitle': 'Loading...',
  'profile.loadingBody': 'Loading profile...',
  'profile.errorLoading': 'Error loading profile',
  'profile.personalInfo': 'Personal Information',
  'profile.editProfile': 'Edit Profile',
  'profile.security': 'Security',
  'profile.securityDesc': 'Update your password to keep your account secure.',
  'profile.changePassword': 'Change Password',
  'profile.dangerZone': 'Danger Zone',
  'profile.deleteAccount': 'Delete Account',
  'profile.deleteAccountDesc':
    'Permanently delete your account and all associated data. This action cannot be undone.',
  'profile.deleteAccountConfirmTitle': 'Delete Your Account',
  'profile.deleteAccountConfirmText':
    'This will permanently delete your account, remove you from any businesses, and erase all your data. This cannot be undone.',
  'profile.deleteAccountCreatorWarning':
    'You are the creator of this business. Deleting your account will permanently delete the entire business including all inventory, transactions, imports, and team data. All team members will be removed.',
  'profile.deleteAccountPasswordLabel': 'Enter your password to confirm',
  'profile.deleteAccountButton': 'Permanently Delete Account',
  'profile.deleteAccountDeleting': 'Deleting...',
  'profile.deleteAccountSuccess': 'Account deleted successfully.',

  // ============================================
  // Error Messages
  // ============================================
  'error.2000': 'Operation successful',
  'error.2001': 'Created successfully',
  'error.2002': 'Completed successfully',
  'error.4000': 'Invalid request data. Please check your input.',
  'error.4001': 'Invalid query parameters.',
  'error.4002': 'Invalid email or password. Please try again.',
  'error.4003': 'Authentication required. Please log in.',
  'error.4009': 'Invalid password format.',
  'error.4004':
    'This email is already registered. Please use a different email or log in.',
  'error.4005': 'Invalid password reset token.',
  'error.4006': 'Password reset token is missing.',
  'error.4007': 'Password reset token has expired.',
  'error.4101': 'Invalid or expired access token. Please log in again.',
  'error.4104': 'Session has expired. Please log in again.',
  'error.4105':
    'You were logged out because your account was signed in from another location.',
  'error.4303': 'Security token mismatch. Please refresh and try again.',
  'error.4304': "You don't have permission to perform this action.",
  'error.4305':
    'No business associated with your account. Please create or join a business.',
  'error.4306': 'Invalid or expired verification link. Please sign up again.',
  'error.4307': 'Your verification link has expired. Please sign up again.',
  'error.4308':
    'Your account has been deactivated. Please contact your administrator.',
  'error.4309': "You don't have the required permission for this action.",
  'error.4310': 'You are not assigned to this storehouse.',
  'error.4311':
    'No storehouses are assigned to your account. Contact your administrator.',
  'error.4312': 'This action requires admin privileges.',
  'error.4313': 'You cannot perform this action on your own account.',
  'error.4314': 'This account cannot be modified.',
  'error.4315': 'The business owner cannot be modified.',
  'error.4290': 'Too many requests. Please try again later.',
  'error.4020': 'A subscription is required. Please set up your plan.',
  'error.4021': 'Your subscription has expired. Please renew to continue.',
  'error.4022':
    'You have reached the limit for your current plan. Upgrade for more capacity.',
  'error.4023':
    'This feature is not available on your current plan. Please upgrade.',
  'error.4024':
    "Cannot downgrade — your current usage exceeds the target plan's limits.",
  'error.4025':
    'This storehouse is locked due to a plan downgrade. Upgrade your plan to unlock it.',
  'error.4400': 'User not found.',
  'error.4401': 'Business not found.',
  'error.4402': 'Storehouse not found.',
  'error.4403': 'Item not found.',
  'error.4404': 'Partner not found.',
  'error.4405': 'Transaction not found.',
  'error.4406': 'Import record not found.',
  'error.4410': 'Membership not found.',
  'error.4411': 'Session not found.',
  'error.4412': 'Role not found.',
  'error.4413':
    'Cannot delete storehouse — it still has items. Move or delete the items first.',
  'error.5000': 'Server error occurred. Please try again later.',
  'error.6000': 'Email service is unavailable. Please try again later.',
  'error.7000': 'Database error occurred. Please try again.',
  'error.7001': 'Invalid database request.',
  'error.7002': 'Database is temporarily unavailable.',
  'error.7003': 'Data validation failed. Please check your input.',
  'error.7004': 'This record already exists.',
  'error.unknown': 'An unexpected error occurred. Please try again.',

  // Billing — Banner
  'billing.daysRemaining': '— {days} day(s) remaining.',
  'billing.contactAdminResolve': 'Contact your admin to resolve.',
  'billing.reviewOrUpgrade': 'Review your plan or upgrade to cancel.',
  'billing.storehousesLocked': '{n} storehouse(s) locked',
  'billing.dueToPlanDowngrade': 'due to plan downgrade.',
  'billing.contactAdminUnlock': 'Contact your admin to unlock.',
  'billing.upgradePlanUnlock': 'Upgrade your plan to unlock them.',
  'billing.goToBilling': 'Go to Billing →',

  // Swap Modal
  'swap.title': 'Swap Resources',
  'swap.description':
    'Swap which storehouses are locked or which users are deactivated. Each swap is 1-for-1 (net-zero). Limited to 2 swaps per day.',
  'swap.storehouses': 'Storehouses',
  'swap.users': 'Users',
  'swap.loadFailed': 'Failed to load swap candidates. Please try again.',
  'swap.emptyState': 'No locked storehouses or deactivated users to swap.',
  'swap.unlockCurrentlyLocked': 'Unlock (currently locked)',
  'swap.lockCurrentlyActive': 'Lock (currently active)',
  'swap.reactivateCurrentlyDeactivated': 'Reactivate (currently deactivated)',
  'swap.deactivateCurrentlyActive': 'Deactivate (currently active)',
  'swap.dailyLimit': '2 swaps per day limit',
  'swap.summary': 'Summary:',
  'swap.storehouseSummary': 'Lock "{lockName}" → Unlock "{unlockName}"',
  'swap.userSummary':
    'Deactivate "{deactivateName}" → Reactivate "{reactivateName}"',
  'swap.created': 'Created',
  'swap.active': 'Active',
  'swap.swapping': 'Swapping...',
  'swap.confirmSwap': 'Confirm Swap',

  // Downgrade Modal
  'downgrade.title': 'Downgrade to {tier}',
  'downgrade.loading': 'Loading requirements...',
  'downgrade.loadFailed':
    'Failed to load downgrade requirements. Please try again.',
  'downgrade.noChangesNeeded':
    'No changes needed — your current usage fits the {tier} plan.',
  'downgrade.downgrading': 'Downgrading...',
  'downgrade.confirm': 'Confirm Downgrade',

  // Upgrade Prompt
  'upgrade.featureNotAvailable':
    'This feature is not available on your current plan.',
  'upgrade.barcodePrintRequired':
    'Barcode & QR label printing is available on Pro and Enterprise plans.',
  'upgrade.limitReached':
    "You've reached the limit ({usage}). Upgrade to increase your capacity.",
  'upgrade.default': 'Upgrade your plan to unlock this feature.',
  'upgrade.goToBilling': 'Go to Billing →',

  // Error Titles
  'errorTitle.planLimit': 'Plan Limit Reached',
  'errorTitle.auth': 'Authentication Error',
  'errorTitle.cannotDelete': 'Cannot Delete',
  'errorTitle.notFound': 'Not Found',
  'errorTitle.server': 'Server Error',
  'errorTitle.email': 'Email Error',
  'errorTitle.database': 'Database Error',
  'errorTitle.rateLimit': 'Rate Limit Exceeded',
  'errorTitle.validation': 'Validation Error',

  // Activity Log
  'activity.title': 'Activity Log',
  'activity.subtitle':
    'Unified view of all business transactions, imports, transfers, and stock adjustments',
  'activity.tabs.all': 'All',
  'activity.tabs.orders': 'Orders',
  'activity.tabs.imports': 'Imports',
  'activity.tabs.transfers': 'Transfers',
  'activity.tabs.adjustments': 'Adjustments',
  'activity.filters.allTypes': 'All Types',
  'activity.filters.search': 'Search by ID, partner, item name...',
  'activity.filters.dateFrom': 'From',
  'activity.filters.dateTo': 'To',
  'activity.filters.createdBy': 'Created By',
  'activity.filters.allUsers': 'All Users',
  'activity.filters.reset': 'Reset Filters',
  'activity.filters.advanced': 'Filters',
  'activity.type.order': 'Order',
  'activity.type.import': 'Import',
  'activity.type.transfer': 'Transfer',
  'activity.type.adjustment': 'Adjustment',
  'activity.timeline.empty': 'No activity found',
  'activity.timeline.emptyHint': 'Try changing your filters or date range',
  'activity.timeline.loadMore': 'Load More',
  'activity.timeline.items': '{count} item(s)',
  'activity.timeline.units': '{count} unit(s)',
  'activity.timeline.from': 'From',
  'activity.timeline.to': 'To',
  'activity.timeline.by': 'by',
  'activity.timeline.completedBy': 'Completed by',
  'activity.timeline.createdBy': 'Created by',
  'activity.detail.id': 'Record ID',
  'activity.detail.partner': 'Partner',
  'activity.detail.createdAt': 'Created',
  'activity.detail.updatedAt': 'Last updated',

  // Adjustments
  'adjustments.title': 'Stock Adjustments',
  'adjustments.newAdjustment': 'New Adjustment',
  'adjustments.item': 'Item',
  'adjustments.quantity': 'Quantity',
  'adjustments.reason': 'Reason',
  'adjustments.notes': 'Notes',
  'adjustments.previous': 'Previous',
  'adjustments.new': 'New',
  'adjustments.change': 'Change',
  'adjustments.performedBy': 'Performed By',
  'adjustments.date': 'Date',
  'adjustments.allReasons': 'All Reasons',
  'adjustments.searchPlaceholder': 'Search by ID...',
  'adjustments.creating': 'Creating...',
  'adjustments.createSuccess': 'Stock adjusted successfully',
  'adjustments.createError': 'Failed to adjust stock',
  'adjustments.reason.supplier-delivery': 'Supplier Delivery',
  'adjustments.reason.return': 'Return',
  'adjustments.reason.manual-count': 'Manual Count',
  'adjustments.reason.sale': 'Sale',
  'adjustments.reason.damage': 'Damage',
  'adjustments.reason.theft': 'Theft',
  'adjustments.empty': 'No adjustments found',
  'adjustments.emptyHint':
    'Stock adjustments will appear here when items are manually adjusted',
  'adjustments.selectItem': 'Select an item',
  'adjustments.addStock': 'Add Stock',
  'adjustments.removeStock': 'Remove Stock',
  'adjustments.notesPlaceholder': 'Optional notes about this adjustment...',
  'adjustments.quantityPlaceholder': 'Enter quantity',

  // ── Audit Log ──────────────────────────────────────
  'auditLog.title': 'Audit Log',
  'auditLog.subtitle':
    'Track catalog and configuration changes across your business',
  'auditLog.showFilters': 'Show Filters',
  'auditLog.hideFilters': 'Hide Filters',
  'auditLog.clearFilters': 'Clear Filters',
  'auditLog.searchPlaceholder': 'Search by name, user...',
  'auditLog.allActions': 'All Actions',
  'auditLog.allEntities': 'All Entities',
  'auditLog.empty': 'No audit log entries found',
  'auditLog.error': 'Failed to load audit log',
  'auditLog.retry': 'Try again',
  'auditLog.showing': 'Showing',
  'auditLog.of': 'of',
  'auditLog.prev': 'Previous',
  'auditLog.next': 'Next',

  // Audit Log — Column headers
  'auditLog.col.when': 'When',
  'auditLog.col.user': 'User',
  'auditLog.col.action': 'Action',
  'auditLog.col.description': 'Description',
  'auditLog.col.details': 'Details',

  // Audit Log — Actions
  'auditLog.action.create': 'Created',
  'auditLog.action.update': 'Updated',
  'auditLog.action.delete': 'Deleted',
  'auditLog.action.bulkCreate': 'Bulk Created',
  'auditLog.action.bulkUpdate': 'Bulk Updated',
  'auditLog.action.bulkDelete': 'Bulk Deleted',
  'auditLog.action.assignRoles': 'Assigned Roles',
  'auditLog.action.assignStorehouses': 'Assigned Storehouses',
  'auditLog.action.changeAppRole': 'Changed Role',
  'auditLog.action.deactivate': 'Deactivated',
  'auditLog.action.reactivate': 'Reactivated',
  'auditLog.action.invite': 'Invited',

  // Audit Log — Entities
  'auditLog.entity.Item': 'Item',
  'auditLog.entity.StoreHouse': 'Storehouse',
  'auditLog.entity.User': 'User',
  'auditLog.entity.Business': 'Business',
  'auditLog.entity.CustomRole': 'Role',
  'auditLog.entity.Partner': 'Partner',
  'auditLog.entity.PendingUser': 'Invited User',

  // Audit Log — Detail modal
  'auditLog.detail.summary': 'Summary',
  'auditLog.detail.user': 'Performed by',
  'auditLog.detail.target': 'Target',
  'auditLog.detail.targetId': 'Target ID',
  'auditLog.detail.count': 'Count',
  'auditLog.detail.details': 'Details',
  'auditLog.detail.timestamp': 'Timestamp',

  // ============================================
  // Public Layout
  // ============================================
  'public.nav.pricing': 'Pricing',
  'public.nav.login': 'Log in',
  'public.nav.signup': 'Start for free',
  'public.nav.brand': 'khoai.io',
  'public.footer.copyright': '© 2026 khoai.io. All rights reserved.',
  'public.footer.privacy': 'Privacy Policy',
  'public.footer.terms': 'Terms of Service',
  'public.footer.billingTerms': 'Billing Terms',
  'public.footer.contact': 'Contact',

  // ============================================
  // Landing Page
  // ============================================

  // Hero — Direct & Professional (B2B)
  'landing.hero.title': "Don't let stockouts evaporate your revenue.",
  'landing.hero.subtitle':
    'Empty shelves cost businesses an average of 4% in annual revenue. khoai.io monitors every SKU across your warehouses and stores in real time — alerting you before a stockout so you can restock and keep the sale.',
  'landing.hero.getStarted': 'Try Free, No Credit Card',
  'landing.hero.viewPricing': 'View Pricing',
  'landing.hero.trust1': 'Free forever plan',
  'landing.hero.trust2': '30-day Pro trial',
  'landing.hero.trust3': 'No credit card required',
  'landing.hero.img.inventory':
    'khoai.io inventory management dashboard showing stock levels across warehouses',
  'landing.hero.img.mobile':
    'khoai.io mobile app for on-the-go inventory management and barcode scanning',

  // Hero flow diagram
  'landing.hero.flow.label': 'try khoAI',
  'landing.hero.flow.done': '✓ done',
  'landing.hero.flow.importBtn': 'Import Data',
  'landing.hero.flow.tableProduct': 'Product',
  'landing.hero.flow.tableQty': 'Qty',
  'landing.hero.flow.tablePrice': 'Price',
  'landing.hero.flow.row1Sku': 'USB Cable',
  'landing.hero.flow.row1Price': '$4.50',
  'landing.hero.flow.row2Sku': 'Sensor v2',
  'landing.hero.flow.row2Price': '$32.00',
  'landing.hero.flow.row3Sku': 'Label roll',
  'landing.hero.flow.row3Price': '$1.20',
  'landing.hero.flow.sellBtn': 'Sell',
  'landing.hero.flow.revenueLabel': 'Revenue today',
  'landing.hero.flow.revenueAmount': '+$1,964.00',
  'landing.hero.flow.revenueEmpty': '$ —',
  'landing.hero.flow.revenueDetail': '↑ 3 orders fulfilled · 780 units sold',

  // How It Works — Import → Thresholds → Alert
  'landing.howItWorks.title': 'Up and running in under 15 minutes',
  'landing.howItWorks.subtitle':
    'No consultants. No complex setup. Connect your catalog, set your thresholds, and khoai.io handles the rest — 24/7.',
  'landing.howItWorks.step1.title': '1. Import your catalog',
  'landing.howItWorks.step1.desc':
    'Upload a CSV or add items manually. Set up your inventory management system across warehouses and stores, and invite your team — most businesses are operational in under 15 minutes.',
  'landing.howItWorks.step2.title': '2. Set thresholds once',
  'landing.howItWorks.step2.desc':
    'Define low-stock and reorder levels per SKU. Our engine watches every unit across every location — 24/7, no spreadsheet required.',
  'landing.howItWorks.step3.title': '3. Restock before customers notice',
  'landing.howItWorks.step3.desc':
    'The moment stock dips below your threshold, you get notified by email and in-app. Act on real data — and never lose a sale to a preventable stockout.',

  // Features
  'landing.features.title':
    'Everything you need for multi-location inventory management',
  'landing.features.subtitle':
    'From warehouses and store floors to barcode operations, khoai.io gives your team real-time inventory management tools to stay organized and efficient across a wide range of products that require regular audits.',
  'landing.feature.warehouse.title':
    'Multi-location inventory management across warehouses and stores',
  'landing.feature.warehouse.desc':
    'Manage stock across multiple warehouses and stores from a single dashboard. Always know what you have and where it is.',
  'landing.feature.orders.title': 'Orders and transactions',
  'landing.feature.orders.desc':
    'Track purchases, sales, and adjustments with full transaction history and audit trails for products that need tight controls.',
  'landing.feature.team.title': 'Team roles and permissions',
  'landing.feature.team.desc':
    'Invite your team and assign granular permissions with custom roles tailored to your workflow.',
  'landing.feature.transfers.title': 'Stock transfers',
  'landing.feature.transfers.desc':
    'Move inventory between storehouses seamlessly with tracked transfer records.',
  'landing.feature.analytics.title': 'Analytics and intelligence',
  'landing.feature.analytics.desc':
    'Surface dead stock, predict low-stock events, and prioritize restocks with built-in intelligence reports.',
  'landing.feature.barcode.title': 'Barcode and QR labels',
  'landing.feature.barcode.desc':
    'Generate and print barcode or QR code labels for your items directly from the app.',
  'landing.feature.mobile.title': 'Mobile app',
  'landing.feature.mobile.desc':
    'Scan barcodes, check stock levels, and manage inventory on the go from your phone.',
  'landing.feature.import.title': 'CSV import and export',
  'landing.feature.import.desc':
    'Bulk-import your existing catalog or export your data at any time. Your data is always yours.',

  // Problem → Solution
  'landing.problem.title':
    'The hidden cost of spreadsheet-based inventory management',
  'landing.problem.subtitle':
    "Businesses lose an average of 4% of annual revenue to stockouts and overstock. If you're still managing inventory in spreadsheets, here's what you're missing:",
  // Story split section
  'landing.story.eyebrow': 'A day in the life',
  'landing.story.imgAlt':
    'An inventory specialist working at a modern office setup with multiple monitors',
  'landing.story.title': 'From guesswork to control — in one shift',
  'landing.story.lead':
    'Meet Minh. She manages stock for three locations and used to lose hours every week hunting down spreadsheet errors. With khoai.io, her mornings look completely different.',
  'landing.story.p1heading': 'Instant visibility, zero hunting',
  'landing.story.p1body':
    'Minh opens khoai.io and sees every location at a glance. Low stock at Branch 2? An alert already went out. No calls, no digging.',
  'landing.story.p2heading': 'Errors caught before they cost money',
  'landing.story.p2body':
    'Discrepancies surface automatically. Every adjustment is timestamped and attributed — Minh knows exactly what changed, when, and who did it.',
  'landing.story.p3heading': 'The whole team moves together',
  'landing.story.p3body':
    'Her team works in their own lanes with role-based access. No accidental overwrites, no one locked out. Restocks get approved in minutes, not days.',
  'landing.story.cta': 'See how it works →',

  // Pain Feed marquee
  'landing.painfeed.a1': "I don't know what's actually in stock",
  'landing.painfeed.a2': 'Someone updated the wrong sheet again',
  'landing.painfeed.a3': "Can't tell who made that change",
  'landing.painfeed.a4': 'We found the error three weeks later',
  'landing.painfeed.a5': 'Two people edited different versions',
  'landing.painfeed.b1': 'No idea which location has the item',
  'landing.painfeed.b2': 'Manual recount every single month',
  'landing.painfeed.b3': 'The formula was wrong for six months',
  'landing.painfeed.b4': 'Lost a sale because stock showed wrong',
  'landing.painfeed.b5': 'No audit trail when something goes wrong',

  'landing.problem.eyebrow': 'Compare',
  'landing.problem.item1': 'Real-time stock levels across all locations',
  'landing.problem.item2': 'Automatic low-stock and dead-stock alerts',
  'landing.problem.item3': 'Audit trails for every transaction',
  'landing.problem.item4':
    'Role-based access so your team can collaborate safely',
  'landing.problem.cta': 'See how khoai.io solves this →',
  'landing.showcase.smartAlerts': 'Low-stock smart alerts',
  'landing.showcase.forecast':
    'Inventory forecasting and restock recommendations',

  // Built With — infrastructure trust
  'landing.builtWith.title': 'Built on infrastructure you can trust',
  'landing.builtWith.subtitle':
    'We use the same tools trusted by Fortune 500 companies so your data is secure, fast, and always available.',
  'landing.builtWith.item1.name': 'MongoDB Atlas',
  'landing.builtWith.item1.desc': 'Encrypted database hosting',
  'landing.builtWith.item2.name': 'Xendit',
  'landing.builtWith.item2.desc': 'Secure payment processing',
  'landing.builtWith.item3.name': 'Vercel',
  'landing.builtWith.item3.desc': 'Global edge hosting',
  'landing.builtWith.item4.name': 'TLS / HTTPS',
  'landing.builtWith.item4.desc': 'Encrypted in transit',
  'landing.builtWith.item5.name': 'Railway',
  'landing.builtWith.item5.desc': 'Backend infrastructure',

  // Founder access / transparency
  'landing.founder.title': 'Built by a real person, not a faceless corporation',
  'landing.founder.subtitle':
    'khoai.io is an independent product built with care. As an early user, you get direct access to the person building it.',
  'landing.founder.chat': 'Chat with the founder',
  'landing.founder.roadmap': 'View the public roadmap',
  'landing.founder.chatDesc':
    'Email the founder directly. No ticket queues — real conversations.',
  'landing.founder.roadmapDesc':
    "See exactly what we're building next. Suggest features. Vote on priorities.",
  'landing.founder.social': 'Follow updates on social media',
  'landing.founder.socialDesc':
    'Public social channels are being set up. We will publish product updates, release notes, and roadmap highlights there.',
  'landing.founder.socialCta': 'Social links coming soon',

  // CTA
  'landing.cta.title': 'Stop losing revenue to empty shelves.',
  'landing.cta.subtitle':
    'Free plan covers 1 location and up to 500 SKUs — forever. Ready for more? Try Pro free for 30 days. No credit card. Cancel anytime.',
  'landing.cta.button': 'Start Free — No Credit Card',

  // Explore / Feature Categories
  'landing.explore.title': 'Explore the product in detail',
  'landing.explore.subtitle':
    'Browse dedicated feature pages for each major capability in the khoai.io platform.',
  'landing.explore.category01': 'Core operations',
  'landing.explore.category02': 'AI & intelligence',
  'landing.explore.category03': 'Mobile & barcode',
  'landing.explore.category04': 'Team & access',
  'landing.explore.inventory': 'Inventory Management',
  'landing.explore.inventoryDesc':
    'Warehouse and in-store stock tracking, transfers, transaction history, and cycle-count workflows for products that need frequent auditing.',
  'landing.explore.analytics': 'AI & Analytics',
  'landing.explore.analyticsDesc':
    'Forecasting, low-stock alerts, and restock recommendations to support faster operational decisions.',
  'landing.explore.barcode': 'Barcode & Mobile',
  'landing.explore.barcodeDesc':
    'Barcode generation and mobile inventory workflows for faster floor-level execution.',
  'landing.explore.team': 'Team & Permissions',
  'landing.explore.teamDesc':
    'Role-based access and operational accountability for secure collaboration across teams.',

  // Dashboard Mockup
  'landing.dashboard.title': 'khoai.io inventory cockpit',
  'landing.dashboard.totalSku': 'Total SKU',
  'landing.dashboard.stockAtRisk': 'Stock at Risk',
  'landing.dashboard.forecastedDemand': 'Forecasted Demand',
  'landing.dashboard.inventoryTrend': 'Inventory Trend & Forecast',
  'landing.dashboard.next14Days': 'Next 14 days',
  'landing.dashboard.topCriticalSkus': 'Top Critical SKUs',
  'landing.dashboard.reorderIn48h': 'Reorder in 48h',
  'landing.dashboard.sku': 'SKU',
  'landing.dashboard.onHand': 'On hand',
  'landing.dashboard.min': 'Min',
  'landing.dashboard.lowStockAlerts': 'Low Stock Alerts',
  'landing.dashboard.urgent': 'urgent',
  'landing.dashboard.warehouse': 'Warehouse',
  'landing.dashboard.stockByLocation': 'Stock by Location',
  'landing.dashboard.aiRestockRecommendation': 'AI Restock Recommendation',
  'landing.dashboard.reviewSuggestion': 'Review suggestion',

  // Problem Section - Comparison
  'landing.problem.spreadsheetLabel': 'Spreadsheet',
  'landing.problem.khaoioLabel': 'khoai.io',
  'landing.problem.spreadsheetDesc':
    'Delayed updates, manual checks, fragmented ownership.',
  'landing.problem.spreadsheetRow1': 'Delayed updates',
  'landing.problem.spreadsheetRow2': 'Manual checks',
  'landing.problem.spreadsheetRow3': 'Fragmented ownership',
  'landing.problem.khaoioDesc':
    'Live stock view, role-based workflows, automated alerts.',
  'landing.problem.khaoioRow1': 'Live stock, every location',
  'landing.problem.khaoioRow2': 'Role-based workflows',
  'landing.problem.khaoioRow3': 'Automated alerts',
  'landing.problem.khaoioTagline': 'Real-time · Automated · Scalable',
  'landing.problem.impactLabel': 'average annual revenue impact',
  'landing.problem.controlLabel': 'faster decisions with real-time data',
  'landing.problem.comparisonTitle': 'Operational Reliability Comparison',
  'landing.problem.stockVisibility': 'Stock visibility',
  'landing.problem.alertResponseSpeed': 'Alert response speed',
  'landing.problem.auditTraceCompleteness': 'Audit trace completeness',

  // Features Section 1
  'landing.feature.section1.eyebrow': 'Core inventory operations',
  'landing.feature.section1.title':
    'Real-time inventory management across all your locations in one workflow',
  'landing.feature.section1.desc':
    'Manage stock across multiple storehouses from a single dashboard. Always know what you have and where it is. Move inventory between storehouses seamlessly with tracked transfer records.',
  'landing.feature.section1.stockByLocation': 'Stock by location',

  // Features Section 2
  'landing.feature.section2.eyebrow': 'Intelligence and recommendations',
  'landing.feature.section2.title':
    'Prioritize what to restock before revenue is affected',
  'landing.feature.section2.desc':
    'Surface dead stock, predict low-stock events, and prioritize restocks with built-in intelligence reports.',
  'landing.feature.section2.lowStockSmartAlerts': 'Low-stock smart alerts',
  'landing.feature.section2.forecast':
    'Inventory forecasting and restock recommendations',

  // Features Section 3
  'landing.feature.section3.eyebrow': 'Execution and collaboration',
  'landing.feature.section3.title':
    'Enable teams to move faster with control and traceability',
  'landing.feature.section3.desc':
    'Invite your team and assign granular permissions with custom roles tailored to your workflow. Scan barcodes, check stock levels, and manage inventory on the go from your phone.',
  'landing.feature.section3.topCriticalSkus': 'Top critical SKUs',
  'landing.feature.section3.status': 'Status',
  'landing.feature.section3.urgent': 'Urgent',
  'landing.feature.section3.risk': 'Risk',
  'landing.feature.section3.monitor': 'Monitor',

  // Social proof / trust strip
  'landing.stats.item1': 'Real-time sync across all locations',
  'landing.stats.item2': 'Every adjustment fully audit-traced',
  'landing.stats.item3': 'AI-powered restock recommendations',
  'landing.stats.item4': '30-day Pro trial · No credit card',

  // FAQ
  'landing.faq.title': 'Common questions',
  'landing.faq.subtitle': 'Everything you need to decide with confidence.',
  'landing.faq.q1': 'Is khoai.io right for my business size?',
  'landing.faq.a1':
    'khoai.io works for businesses with 1 to 100+ locations — from a single retail shop to a multi-warehouse operation. The Free plan covers one location; Pro scales to as many as you need.',
  'landing.faq.q2': 'How do I get my existing inventory in?',
  'landing.faq.a2':
    'Use our CSV import to bulk-upload your product catalog in minutes. You can also add items one at a time. Most businesses are fully imported from their spreadsheet within an hour.',
  'landing.faq.q3': 'What happens when my 30-day Pro trial ends?',
  'landing.faq.a3':
    'You automatically move back to the Free plan — no charges, no data loss. Your inventory, history, and settings stay intact. Upgrade again anytime.',
  'landing.faq.q4': 'Can I track stock across multiple warehouses and stores?',
  'landing.faq.a4':
    'Yes. Multi-location tracking is a core feature, not a paid add-on. You can monitor stock, process transfers, and audit adjustments across every location from one dashboard.',
  'landing.faq.q5': 'How is my data protected?',
  'landing.faq.a5':
    'Your data is stored on MongoDB Atlas and transmitted over TLS/HTTPS. The platform is designed with widely adopted security practices for business applications, with protections at both application and infrastructure layers.',

  // ============================================
  // Pricing Page
  // ============================================
  'pricing.title': 'Simple, transparent pricing',
  'pricing.subtitle': 'Start free and scale as you grow. No hidden fees.',
  'pricing.monthly': 'Monthly',
  'pricing.annual': 'Annual',
  'pricing.save': 'Save ~17%',
  'pricing.perMonth': '/mo',
  'pricing.billedAnnually': 'Billed annually ({amount}/yr)',
  'pricing.recommended': 'Recommended',
  'pricing.vndNote': 'Pricing in VND available for businesses in Vietnam.',
  'pricing.trust.freePlan': 'Free forever plan',
  'pricing.trust.noCard': 'No credit card to start',
  'pricing.trust.cancel': 'Cancel anytime',

  'pricing.free.name': 'Free',
  'pricing.free.desc':
    'For individuals getting started with inventory tracking.',
  'pricing.free.cta': 'Get Started',
  'pricing.free.f1': '1 storehouse',
  'pricing.free.f2': '1 user',
  'pricing.free.f3': '500 items',
  'pricing.free.f4': '2,000 transactions/mo',
  'pricing.free.f5': 'Basic analytics',

  'pricing.pro.name': 'Pro',
  'pricing.pro.desc':
    'For growing teams that need more capacity and collaboration.',
  'pricing.pro.cta': 'Start Free Trial',
  'pricing.pro.f1': '3 storehouses',
  'pricing.pro.f2': '10 users',
  'pricing.pro.f3': '5,000 items',
  'pricing.pro.f4': '20,000 transactions/mo',
  'pricing.pro.f5': 'Stock transfers',
  'pricing.pro.f6': 'Custom roles',
  'pricing.pro.f7': 'Analytics reports',

  'pricing.enterprise.name': 'Enterprise',
  'pricing.enterprise.desc':
    'For larger organizations with advanced requirements.',
  'pricing.enterprise.cta': 'Contact Us',
  'pricing.enterprise.f1': '10 storehouses',
  'pricing.enterprise.f2': '30 users',
  'pricing.enterprise.f3': '50,000 items',
  'pricing.enterprise.f4': '100,000 transactions/mo',
  'pricing.enterprise.f5': 'Everything in Pro',
  'pricing.enterprise.f6': 'Advanced reports',
  'pricing.enterprise.f7': 'Priority support',

  'pricing.addons.title': 'Need more? Add capacity to any paid plan.',
  'pricing.addons.subtitle':
    'Purchase add-ons to expand your limits without changing plans.',
  'pricing.addon.storehouse.name': 'Extra storehouse',
  'pricing.addon.storehouse.price': '$4.99/mo per storehouse',
  'pricing.addon.users.name': 'Extra users',
  'pricing.addon.users.price': '$2.99/mo per user',
  'pricing.addon.items.name': 'Extra items',
  'pricing.addon.items.price': '$3.99/mo per 1,000 items',
  'pricing.addon.transactions.name': 'Extra transactions',
  'pricing.addon.transactions.price': '$2.99/mo per 5,000 transactions',

  'pricing.faq.title': 'Frequently asked questions',
  'pricing.faq.trial.q': 'What happens when my free trial ends?',
  'pricing.faq.trial.a':
    'When your 30-day Pro trial ends, you can subscribe to keep your Pro features or drop to the Free plan — no credit card is charged if you do nothing. Your data is always preserved.',
  'pricing.faq.change.q': 'Can I change plans at any time?',
  'pricing.faq.change.a':
    'Yes. Upgrades take effect immediately. Downgrades apply at the end of your current billing period.',
  'pricing.faq.payment.q': 'What payment methods do you accept?',
  'pricing.faq.payment.a':
    'We accept bank transfer, e-wallets, and credit cards through our payment processor, Xendit. Available methods vary by region.',
  'pricing.faq.cancel.q': 'Can I cancel my subscription?',
  'pricing.faq.cancel.a':
    "Absolutely. You can cancel from the billing page at any time. You'll retain access until the end of your current billing period.",
  'pricing.faq.refund.q': 'Do you offer refunds?',
  'pricing.faq.refund.a':
    'Monthly plans are not refunded. Annual plans may receive a prorated refund on a case-by-case basis. Contact us to request one.',
  'pricing.faq.vnd.q': 'Is pricing available in VND?',
  'pricing.faq.vnd.a':
    'Yes. Pricing in VND is available for businesses registered in Vietnam. Currency is locked at the time of subscription.',
  'pricing.faq.data.q': 'What happens to my data if I cancel?',
  'pricing.faq.data.a':
    'Your data is retained for 30 days after cancellation so you can export it before it is permanently deleted. You can export at any time from the Settings page.',

  // ============================================
  // Terms of Service (19 sections)
  // ============================================
  'terms.title': 'Terms of Service',
  'terms.effective': 'Effective: May 2026',

  // 1. Acceptance
  'terms.s1.title': '1. Acceptance of Terms',
  'terms.s1.p1':
    'By accessing or using the khoai.io service ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service. These Terms constitute a legally binding agreement between you and khoai.io.',

  // 2. Description of Service
  'terms.s2.title': '2. Description of Service',
  'terms.s2.p1':
    'khoai.io is a cloud-based inventory and store management platform that enables businesses to track stock across multiple storehouses, manage orders and transactions, collaborate with team members, and generate reports. The Service is accessible via web browser and mobile application at',

  // 3. Account Registration
  'terms.s3.title': '3. Account Registration',
  'terms.s3.li1':
    'You must provide accurate and complete information when creating an account.',
  'terms.s3.li2':
    'You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account.',
  'terms.s3.li3':
    'Each account is associated with one business. You may not create multiple accounts for the same business.',
  'terms.s3.li4':
    'You must notify us immediately if you suspect unauthorized access to your account.',

  // 4. Acceptable Use
  'terms.s4.title': '4. Acceptable Use',
  'terms.s4.p1':
    'You agree to use the Service only for lawful purposes. You may not:',
  'terms.s4.li1':
    'Use the Service for any unlawful, fraudulent, or harmful activity',
  'terms.s4.li2': 'Abuse, harass, or interfere with other users of the Service',
  'terms.s4.li3':
    "Attempt to interfere with, disrupt, or overload the Service's infrastructure",
  'terms.s4.li4':
    'Reverse engineer, decompile, or disassemble any part of the Service',
  'terms.s4.li5':
    "Access or attempt to access another user's account without authorization",
  'terms.s4.li6':
    'Use automated tools to scrape, crawl, or extract data from the Service beyond what is available through our export features',

  // 5. Intellectual Property
  'terms.s5.title': '5. Intellectual Property',
  'terms.s5.p1':
    'The khoai.io platform — including its design, code, features, and documentation — is the intellectual property of khoai.io. You may not copy, modify, distribute, or create derivative works from the Service without our express written consent.',
  'terms.s5.p2':
    'You retain full ownership of all data you enter into the Service, including inventory data, transaction records, and business information.',

  // 6. User Data
  'terms.s6.title': '6. User Data',
  'terms.s6.p1':
    'You retain ownership of all data you submit to khoai.io. By using the Service, you grant khoai.io a limited, non-exclusive license to process, store, and display your data solely for the purpose of providing and improving the Service.',
  'terms.s6.p2':
    'We will not access your data except as necessary to operate the Service, provide support at your request, or comply with legal obligations. For more details, see our',

  // 7. Subscription and Billing
  'terms.s7.title': '7. Subscription and Billing',
  'terms.s7.li1':
    'khoai.io offers Free, Pro, and Enterprise plans. Features and limits vary by plan. See our',
  'terms.s7.li1.after': 'for current rates.',
  'terms.s7.li2':
    'Paid plans are billed in advance on a monthly or annual cycle. Annual plans receive a discount.',
  'terms.s7.li3':
    'Add-ons for additional capacity (storehouses, users, items, transactions) are available for paid plans and billed monthly.',
  'terms.s7.li4':
    'Subscriptions renew automatically at the end of each billing period.',
  'terms.s7.li5':
    'Payments are processed securely through Xendit. Available payment methods include bank transfer, e-wallets, and credit cards (availability varies by region).',
  'terms.s7.li6':
    'The Pro plan includes a 30-day free trial at no charge — no credit card required. If you do not subscribe before the trial ends, your account automatically reverts to the Free plan. Each business is eligible for one free trial.',
  'terms.s7.p1': 'For full billing details, see our',

  // 8. Cancellation and Refunds
  'terms.s8.title': '8. Cancellation and Refunds',
  'terms.s8.li1':
    'You may cancel your subscription at any time from the billing page in your account settings.',
  'terms.s8.li2':
    'Upon cancellation, you will retain access to paid features until the end of your current billing period.',
  'terms.s8.li3': 'Monthly plans are not eligible for refunds.',
  'terms.s8.li4':
    'Annual plans may receive a prorated refund on a case-by-case basis at our discretion. Contact',
  'terms.s8.li4.after': 'for refund requests.',

  // 9. Service Availability
  'terms.s9.title': '9. Service Availability',
  'terms.s9.p1':
    'We strive to maintain the highest possible uptime for the Service. For paid plans, we target 99.9% availability. However, the Free plan does not include an SLA guarantee.',
  'terms.s9.p2':
    'We may occasionally take the Service offline for maintenance, updates, or emergency repairs. We will make reasonable efforts to provide advance notice of planned downtime.',

  // 10. Limitation of Liability
  'terms.s10.title': '10. Limitation of Liability',
  'terms.s10.p1':
    'The Service is provided on an "as is" and "as available" basis. khoai.io makes no warranties, express or implied, regarding the Service\'s reliability, accuracy, or fitness for a particular purpose.',
  'terms.s10.p2':
    "To the maximum extent permitted by law, khoai.io's total liability to you for any claims arising from or related to the Service shall not exceed the total amount you have paid to khoai.io in the 12 months preceding the claim.",
  'terms.s10.p3':
    'khoai.io shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data, revenue, or business opportunities.',

  // 11. Indemnification (NEW)
  'terms.s11.title': '11. Indemnification',
  'terms.s11.p1':
    'You agree to indemnify, defend, and hold harmless khoai.io, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or in any way connected with your access to or use of the Service, your violation of these Terms, or your infringement of any third-party rights.',

  // 12. Force Majeure (NEW)
  'terms.s12.title': '12. Force Majeure',
  'terms.s12.p1':
    'khoai.io shall not be liable for any failure or delay in performing its obligations under these Terms if such failure or delay results from circumstances beyond its reasonable control, including but not limited to natural disasters, acts of government, internet or infrastructure outages, cyberattacks, pandemics, or other force majeure events.',

  // 13. Account Termination
  'terms.s13.title': '13. Account Termination',
  'terms.s13.li1':
    'We may suspend or terminate your account if you violate these Terms, abuse the Service, or engage in activity that harms khoai.io or other users.',
  'terms.s13.li2':
    'You may delete your account at any time. Upon deletion, all associated data (business, inventory, transactions, and team records) will be permanently removed after a 30-day grace period.',
  'terms.s13.li3':
    'Termination does not relieve you of any outstanding payment obligations.',

  // 14. Governing Law and Dispute Resolution (expanded)
  'terms.s14.title': '14. Governing Law and Dispute Resolution',
  'terms.s14.p1':
    'These Terms shall be governed by and construed in accordance with the laws of the Socialist Republic of Vietnam, without regard to conflict-of-law principles.',
  'terms.s14.p2':
    'Any disputes arising from or relating to these Terms or the Service shall first be resolved through good-faith negotiation between the parties. If the dispute cannot be resolved through negotiation within 30 days, either party may submit the dispute to the competent courts of Vietnam for resolution.',

  // 15. Assignment (NEW)
  'terms.s15.title': '15. Assignment',
  'terms.s15.p1':
    'You may not assign or transfer your rights or obligations under these Terms without the prior written consent of khoai.io. khoai.io may assign its rights and obligations under these Terms to a successor entity in connection with a merger, acquisition, or sale of all or substantially all of its assets, provided that the successor agrees to be bound by these Terms.',

  // 16. Changes to Terms
  'terms.s16.title': '16. Changes to Terms',
  'terms.s16.p1':
    "We may update these Terms from time to time. When we make material changes, we will provide at least 30 days' notice via email. Your continued use of the Service after the changes take effect constitutes your acceptance of the revised Terms.",

  // 17. Severability (NEW)
  'terms.s17.title': '17. Severability',
  'terms.s17.p1':
    'If any provision of these Terms is found to be invalid, illegal, or unenforceable by a court of competent jurisdiction, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable while preserving the original intent.',

  // 18. Entire Agreement (NEW — includes waiver)
  'terms.s18.title': '18. Entire Agreement',
  'terms.s18.p1':
    'These Terms, together with the Privacy Policy and Billing Terms, constitute the entire agreement between you and khoai.io regarding the use of the Service. They supersede all prior or contemporaneous agreements, communications, and proposals, whether oral or written.',
  'terms.s18.p2':
    'The failure of khoai.io to exercise or enforce any right or provision of these Terms shall not constitute a waiver of such right or provision. Any waiver of any provision of these Terms will be effective only if in writing and signed by khoai.io.',

  // 19. Contact
  'terms.s19.title': '19. Contact',
  'terms.s19.p1':
    'If you have any questions about these Terms, please contact us at:',

  // ============================================
  // Privacy Policy (14 sections)
  // ============================================
  'privacy.title': 'Privacy Policy',
  'privacy.lastUpdated': 'Last updated: May 2026',

  // 1. Introduction
  'privacy.s1.title': '1. Introduction',
  'privacy.s1.p1':
    'khoai.io is a cloud-based inventory and store management platform that helps businesses track stock, manage orders, and collaborate with their teams. This Privacy Policy explains how khoai.io ("we", "us", or "our") collects, uses, stores, and protects your personal and business information when you use our website, mobile application, and services at',
  'privacy.s1.p2':
    'By creating an account or using our services, you agree to the practices described in this policy. If you do not agree, please do not use khoai.io.',

  // 2. Information We Collect
  'privacy.s2.title': '2. Information We Collect',
  'privacy.s2.accountData': 'Account Data',
  'privacy.s2.account.li1': 'Full name',
  'privacy.s2.account.li2': 'Email address',
  'privacy.s2.account.li3': 'Phone number (optional)',
  'privacy.s2.account.li4':
    'Password (stored as a secure hash — we never store plaintext passwords)',
  'privacy.s2.businessData': 'Business Data',
  'privacy.s2.business.li1': 'Business name',
  'privacy.s2.business.li2': 'Business address',
  'privacy.s2.business.li3': 'Business phone number',
  'privacy.s2.inventoryData': 'Inventory and Transaction Data',
  'privacy.s2.inventory.li1':
    'Items (names, SKUs, descriptions, prices, quantities)',
  'privacy.s2.inventory.li2': 'Transactions (purchases, sales, adjustments)',
  'privacy.s2.inventory.li3': 'Transfers between storehouses',
  'privacy.s2.inventory.li4': 'Supplier and client records',
  'privacy.s2.usageData': 'Usage Data',
  'privacy.s2.usage.li1': 'IP address',
  'privacy.s2.usage.li2': 'Browser type and version',
  'privacy.s2.usage.li3': 'Pages visited and features used',
  'privacy.s2.usage.li4': 'Timestamps of access',
  'privacy.s2.cookiesData': 'Cookies',
  'privacy.s2.cookies.li1': 'Session cookie (for authentication)',
  'privacy.s2.cookies.li2': 'CSRF cookie (for security)',

  // 3. How We Use Your Information
  'privacy.s3.title': '3. How We Use Your Information',
  'privacy.s3.li1': 'Provide, operate, and maintain the khoai.io service',
  'privacy.s3.li2': 'Authenticate your identity and manage sessions',
  'privacy.s3.li3':
    'Send transactional emails (account verification, password resets, team invitations)',
  'privacy.s3.li4': 'Process billing and subscription management',
  'privacy.s3.li5':
    'Improve the service through aggregated, anonymized usage analytics',
  'privacy.s3.li6': 'Respond to support requests',

  // 4. Legal Basis for Processing (NEW)
  'privacy.s4.title': '4. Legal Basis for Processing',
  'privacy.s4.p1':
    'We process your personal data based on the following legal grounds:',
  'privacy.s4.li1':
    'Contract performance — to provide the khoai.io service you subscribed to, including account management, billing, and support',
  'privacy.s4.li2':
    'Legitimate interest — to improve and secure our services, prevent fraud, and analyze aggregated usage patterns',
  'privacy.s4.li3':
    'Legal obligation — to comply with applicable laws, regulations, and legal processes',
  'privacy.s4.li4':
    'Consent — where you have given explicit consent for specific processing activities, such as receiving marketing communications',

  // 5. Data Sharing
  'privacy.s5.title': '5. Data Sharing',
  'privacy.s5.p1':
    'We do not sell, rent, or trade your personal or business data to third parties.',
  'privacy.s5.p2':
    'We share data only with the following service providers, solely to operate the khoai.io service:',
  'privacy.s5.li1': 'Xendit — payment processing for subscriptions and add-ons',
  'privacy.s5.li2': 'Resend / SendGrid — transactional email delivery',
  'privacy.s5.li3':
    'Cloud hosting provider(s) — frontend hosting infrastructure',
  'privacy.s5.li4':
    'Cloud hosting provider(s) — backend hosting infrastructure',
  'privacy.s5.li5': 'MongoDB Atlas — database hosting',
  'privacy.s5.p3':
    'Each provider is contractually obligated to protect your data and use it only for the purposes we specify.',

  // 6. International Data Transfers (NEW)
  'privacy.s6.title': '6. International Data Transfers',
  'privacy.s6.p1':
    'Your data may be stored and processed in jurisdictions outside your country of residence, including but not limited to the United States and Singapore, through our hosting and infrastructure providers.',
  'privacy.s6.p2':
    'We ensure that appropriate safeguards are in place to protect your data in accordance with applicable data protection laws. Where required, we rely on standard contractual clauses or other approved transfer mechanisms.',

  // 7. Data Security
  'privacy.s7.title': '7. Data Security',
  'privacy.s7.p1':
    'We take the security of your data seriously and implement the following measures:',
  'privacy.s7.li1': 'All data is encrypted in transit using TLS (HTTPS)',
  'privacy.s7.li2': 'Data at rest is encrypted via MongoDB Atlas encryption',
  'privacy.s7.li3':
    'Session cookies are httpOnly and secure, preventing client-side access',
  'privacy.s7.li4': 'CSRF protection is enabled on all state-changing requests',
  'privacy.s7.li5': 'Rate limiting is applied to prevent abuse',
  'privacy.s7.li6': 'Passwords are hashed using industry-standard algorithms',

  // 8. Data Retention
  'privacy.s8.title': '8. Data Retention',
  'privacy.s8.li1':
    'Account data is retained while your account is active and for 30 days after account deletion to allow for recovery.',
  'privacy.s8.li2':
    'Inventory, transaction, and business data is permanently deleted when the associated business is deleted.',
  'privacy.s8.li3':
    'Usage logs are retained for up to 90 days for security and debugging purposes, then automatically purged.',

  // 9. Your Rights (expanded)
  'privacy.s9.title': '9. Your Rights',
  'privacy.s9.p1': 'You have the right to:',
  'privacy.s9.li1':
    'Access — view all personal and business data stored in your account',
  'privacy.s9.li2':
    'Correction — update inaccurate information from your account settings',
  'privacy.s9.li3': 'Deletion — delete your account and all associated data',
  'privacy.s9.li4':
    'Data export — export your inventory and transaction data via CSV',
  'privacy.s9.li5':
    'Restrict processing — request that we limit how we use your data in certain circumstances',
  'privacy.s9.li6':
    'Object — object to processing of your data based on legitimate interest',
  'privacy.s9.li7':
    'Lodge a complaint — file a complaint with a data protection authority in your jurisdiction',

  // 10. Cookies
  'privacy.s10.title': '10. Cookies',
  'privacy.s10.p1': 'khoai.io uses the following cookies:',
  'privacy.s10.li1':
    'Session cookie — an httpOnly cookie used to maintain your authenticated session',
  'privacy.s10.li2':
    'CSRF cookie — used to protect against cross-site request forgery attacks',
  'privacy.s10.p2':
    'We do not use third-party tracking cookies, advertising cookies, or analytics cookies from external providers.',

  // 11. Children
  'privacy.s11.title': '11. Children',
  'privacy.s11.p1':
    'khoai.io is not intended for use by individuals under the age of 16. We do not knowingly collect personal information from children. If you believe a child under 16 has provided us with personal data, please contact us at',
  'privacy.s11.p1.after': 'and we will promptly delete the information.',

  // 12. Data Breach Notification (NEW)
  'privacy.s12.title': '12. Data Breach Notification',
  'privacy.s12.p1':
    'In the event of a data breach that is likely to result in a risk to your rights and freedoms, we will notify affected users via email within 72 hours of becoming aware of the breach. The notification will include the nature of the breach, the data potentially affected, and the measures taken to address and mitigate the impact.',

  // 13. Changes to This Policy
  'privacy.s13.title': '13. Changes to This Policy',
  'privacy.s13.p1':
    'We may update this Privacy Policy from time to time. When we make material changes, we will notify you by email using the address associated with your account. The updated policy will also be posted on this page with a revised "Last updated" date.',

  // 14. Contact
  'privacy.s14.title': '14. Contact',
  'privacy.s14.p1':
    'If you have any questions or concerns about this Privacy Policy or how we handle your data, please contact us at:',

  // ============================================
  // Billing Terms (16 sections)
  // ============================================
  'billingTerms.title': 'Billing Terms',
  'billingTerms.effective': 'Effective: May 2026',

  // 1. Plans and Pricing
  'billingTerms.s1.title': '1. Plans and Pricing',
  'billingTerms.s1.p1':
    'khoai.io offers three subscription plans: Free, Pro, and Enterprise. Each plan includes different feature limits for storehouses, users, items, and monthly transactions. Current pricing and feature details are published on our',

  // 2. Billing Cycle
  'billingTerms.s2.title': '2. Billing Cycle',
  'billingTerms.s2.p1':
    'Paid subscriptions are billed in advance on either a monthly or annual cycle, depending on the option you select at the time of subscription. Annual billing provides a discount compared to the monthly rate. Your billing cycle starts on the date you subscribe and renews on the same date each period.',

  // 3. Currency
  'billingTerms.s3.title': '3. Currency',
  'billingTerms.s3.p1':
    'Subscriptions are billed in USD or VND, determined by the business region you select during registration. The billing currency is locked at the time of subscription and cannot be changed without canceling and resubscribing.',

  // 4. Payment Methods
  'billingTerms.s4.title': '4. Payment Methods',
  'billingTerms.s4.p1':
    'All payments are processed securely through Xendit, our third-party payment processor. Available payment methods include:',
  'billingTerms.s4.li1': 'Bank transfer',
  'billingTerms.s4.li2': 'E-wallets (where available)',
  'billingTerms.s4.li3': 'Credit and debit cards (where available)',
  'billingTerms.s4.p2':
    'Available payment methods may vary depending on your region. khoai.io does not store payment card details — all sensitive payment information is handled by Xendit.',

  // 5. Add-Ons
  'billingTerms.s5.title': '5. Add-Ons',
  'billingTerms.s5.p1':
    "Paid plan subscribers can purchase add-ons to increase their capacity beyond the plan's included limits. Add-ons are available for additional storehouses, users, items, and monthly transactions.",
  'billingTerms.s5.p2':
    "Add-ons are billed monthly, regardless of your subscription's billing cycle. Add-ons can be added or removed at any time and take effect immediately.",

  // 6. Upgrades and Downgrades
  'billingTerms.s6.title': '6. Upgrades and Downgrades',
  'billingTerms.s6.li1':
    "Upgrades take effect immediately. You will be charged a prorated amount for the remainder of your current billing period at the new plan's rate.",
  'billingTerms.s6.li2':
    'Downgrades take effect at the end of your current billing period. You will retain access to higher-tier features until that period ends.',

  // 7. Free Plan and Free Trial
  'billingTerms.s7.title': '7. Free Plan and Free Trial',
  'billingTerms.s7.p1':
    'The Free plan does not require a payment method and is available indefinitely. The Free plan has limited features and capacity. No service level agreement (SLA) is provided for the Free plan.',
  'billingTerms.s7.p2':
    'New Pro subscriptions include a 30-day free trial at no charge. No credit card or payment method is required to start a trial.',
  'billingTerms.s7.p3':
    'When the trial ends, you may subscribe to continue Pro features, or your account will automatically revert to the Free plan — your data is always preserved. Each business is eligible for one free trial.',

  // 8. Auto-Renewal
  'billingTerms.s8.title': '8. Auto-Renewal',
  'billingTerms.s8.p1':
    'All paid subscriptions renew automatically at the end of each billing period at the then-current rate. To prevent renewal, you must cancel your subscription before the renewal date. You will receive a reminder email at least 7 days before your subscription renews.',

  // 9. Failed Payments
  'billingTerms.s9.title': '9. Failed Payments',
  'billingTerms.s9.p1':
    'If a payment fails, we will notify you and provide a grace period to update your payment method. If payment is not received after repeated attempts, your account may be suspended. During suspension, you will not be able to access paid features, but your data will be preserved for 30 days. After 30 days of suspension without payment, your account may be downgraded to the Free plan and data exceeding Free plan limits may become inaccessible.',

  // 10. Refunds
  'billingTerms.s10.title': '10. Refunds',
  'billingTerms.s10.li1':
    'Monthly plans: No refunds are provided for monthly subscriptions. You will retain access until the end of your current billing period.',
  'billingTerms.s10.li2':
    'Annual plans: Prorated refunds for unused months may be considered on a case-by-case basis at our discretion. Contact',
  'billingTerms.s10.li2.after': 'to request a refund.',

  // 11. Cancellation
  'billingTerms.s11.title': '11. Cancellation',
  'billingTerms.s11.p1':
    'You may cancel your subscription at any time from the billing page in your account settings. Upon cancellation:',
  'billingTerms.s11.li1':
    'You will retain access to paid features until the end of your current billing period.',
  'billingTerms.s11.li2':
    'After your billing period ends, your account will revert to the Free plan.',
  'billingTerms.s11.li3':
    'Your data will be preserved, but features and limits beyond the Free plan will no longer be accessible.',

  // 12. Tax
  'billingTerms.s12.title': '12. Tax',
  'billingTerms.s12.p1':
    'All prices listed on khoai.io are exclusive of applicable taxes. You are responsible for paying any taxes, duties, or levies imposed by your jurisdiction in connection with your use of the Service.',

  // 13. Invoices and Receipts (NEW)
  'billingTerms.s13.title': '13. Invoices and Receipts',
  'billingTerms.s13.p1':
    'Invoices for subscription payments are generated automatically and available in your billing settings. Invoices include the billing period, plan details, amount charged, and applicable taxes. You may download invoices in PDF format for your records.',

  // 14. Billing Disputes (NEW)
  'billingTerms.s14.title': '14. Billing Disputes',
  'billingTerms.s14.p1':
    'If you believe a charge is incorrect, please contact us at',
  'billingTerms.s14.p1.after':
    'within 30 days of the charge. We will review the dispute and respond within 10 business days. While a dispute is under review, your access to the Service will not be interrupted.',

  // 15. Price Changes
  'billingTerms.s15.title': '15. Price Changes',
  'billingTerms.s15.p1':
    "We may change our pricing from time to time. When we do, we will provide at least 30 days' notice via email. Price changes will apply at your next renewal date. Existing subscriptions will be honored at the current rate until the end of the current billing period.",

  // 16. Contact
  'billingTerms.s16.title': '16. Contact',
  'billingTerms.s16.p1':
    'For billing inquiries, refund requests, or payment issues, please contact us at:',

  // ============================================
  // Feature Pages - Shared/Common
  // ============================================
  'feature.problemSolution': 'Problem and solution',
  'feature.featureBreakdown': 'Feature breakdown',
  'feature.cta': 'Start free',
  'feature.ctaSubtitle':
    'Start with a workflow your team can trust and scale from one warehouse or store to many locations.',

  // Inventory Management Page
  'feature.inventory.tag': 'Inventory Management',
  'feature.inventory.title':
    'One system for every item, every warehouse and store, every movement',
  'feature.inventory.subtitle':
    'From one warehouse or storefront to many, khoai.io gives you live stock counts, traceable adjustments, and controlled transfers — all in one operational record.',
  'feature.inventory.problemDesc1':
    'Most inventory problems come down to one thing: no one knows the exact number. Spreadsheets go stale, locations diverge, and by the time you catch a discrepancy the damage is done.',
  'feature.inventory.problemDesc2':
    'khoai.io makes every stock change an auditable event — typed, timestamped, and tied to the right location. Adjustments run inside atomic transactions so counts are always consistent, and transfers track every step from dispatch to confirmed receipt.',
  'feature.inventory.bullet1':
    'Live quantities per location with low-stock and out-of-stock signals',
  'feature.inventory.bullet2':
    'Adjustments with typed reasons — sale, return, damage, theft, manual count, supplier delivery',
  'feature.inventory.bullet3':
    'Transfers that track pending → in-transit → received, with partial-receive and discrepancy logging',
  'feature.inventory.f1': 'Per-location stock visibility',
  'feature.inventory.f1Desc':
    "See total items, quantities, low-stock count, and today's sales in one summary. Filter by warehouse, store, status, tags, or price range. Each location's access is independently controlled.",
  'feature.inventory.f1Bullet1':
    'One dashboard with total items, on-hand quantity, and stock risk by location',
  'feature.inventory.f1Bullet2':
    'Fast filters for warehouse/store location, stock status, tags, and price range',
  'feature.inventory.f1Bullet3':
    'Built-in low-stock and out-of-stock signals for faster replenishment decisions',
  'feature.inventory.f2': 'Adjustment integrity',
  'feature.inventory.f2Desc':
    'Every quantity change logs a typed reason and runs as an atomic transaction — the item count and adjustment record update together or not at all. That gives your team a clean, traceable history for audits, investigations, and stock recounts.',
  'feature.inventory.f2Bullet1':
    'Each adjustment records previous quantity, new quantity, reason, notes, and who made the change',
  'feature.inventory.f2Bullet2':
    'Typed reasons keep audit history readable across sales, damage, returns, theft, manual counts, and supplier deliveries',
  'feature.inventory.f2Bullet3':
    'Adjustment history can be filtered by item, reason, user, date range, or search for faster investigations',
  'feature.inventory.f3': 'Transfer lifecycle',
  'feature.inventory.f3Desc':
    'Move stock between warehouses and stores with full status tracking from request to receipt. Teams can follow each transfer step-by-step, record discrepancies on arrival, and keep movement history tied to the right locations.',
  'feature.inventory.f3Bullet1':
    'Track each transfer through pending, shipped, in-transit, partially received, completed, or cancelled states',
  'feature.inventory.f3Bullet2':
    'Receive stock partially and log discrepancy reasons such as damaged, missing, wrong item, or quantity mismatch',
  'feature.inventory.f3Bullet3':
    'Review overdue transfers and stalled partial receipts from a dedicated exception inbox',
  'feature.inventory.proTooltip': 'Available on Pro and above',
  'feature.inventory.f4': 'Import your data in minutes',
  'feature.inventory.f4Desc':
    'Just upload your existing spreadsheet. khoai.io maps your columns, validates every row, and loads your inventory — accurately, without any reformatting.',
  'feature.inventory.f4SpreadsheetAlt': 'Raw spreadsheet data',
  'feature.inventory.f4TableAlt': 'Inventory imported into khoai.io',
  'feature.inventory.cta': 'Stop guessing. Start controlling.',

  // AI & Analytics Page
  'feature.analytics.tag': 'AI & Analytics',
  'feature.analytics.title': 'Stop guessing. Start predicting.',
  'feature.analytics.subtitle':
    'Get ahead of stockouts with AI-powered forecasting, smart alerts, and data-driven recommendations made for warehouse and store operators, retailers, and distributors.',
  'feature.analytics.f0': 'Revenue & cost trend',
  'feature.analytics.f0Desc':
    'See exactly where your revenue is growing and where costs are creeping up — broken down by warehouse/store location, category, or time period.',
  'feature.analytics.f0Bullet1':
    'Revenue vs. cost of goods overlaid on a single timeline for fast comparison',
  'feature.analytics.f0Bullet2':
    'Drill down by warehouse/store location, partner, or item category to isolate margin pressure',
  'feature.analytics.f0Bullet3':
    'Period-over-period comparison to spot seasonal shifts and growth trends',
  'feature.analytics.problemDesc1':
    'Traditional approaches rely on historical data and manual thresholds, which fail under new demand patterns, seasonality, or growth.',
  'feature.analytics.problemDesc2':
    'khoai.io continuously learns from your inventory patterns and alerts you weeks in advance so you can act, not react.',
  'feature.analytics.bullet1':
    'Automatic demand forecasting by SKU and location',
  'feature.analytics.bullet2': 'Real-time low-stock and dead-stock alerts',
  'feature.analytics.bullet3':
    'One-click restock recommendations with approval workflows',
  'feature.analytics.f1': 'Forecast by context',
  'feature.analytics.f1Desc':
    'Predict demand based on historical sales, seasonal trends, and location-specific patterns — not just a flat average.',
  'feature.analytics.f1Bullet1':
    'Per-SKU demand curves adjusted for your sales cadence and growth rate',
  'feature.analytics.f1Bullet2':
    'Seasonal and event-based pattern recognition across your full item catalogue',
  'feature.analytics.f1Bullet3':
    'Per-location forecasting so each warehouse and store can run with the right thresholds',
  'feature.analytics.f2': 'Smart low-stock alerts',
  'feature.analytics.f2Desc':
    'Get notified weeks in advance of potential stockouts so restock orders arrive before shelves run empty.',
  'feature.analytics.f2Bullet1':
    'Alert queue ranked by severity — critical, warning, and watch levels',
  'feature.analytics.f2Bullet2':
    'Context-aware thresholds that adapt as demand velocity changes',
  'feature.analytics.f2Bullet3':
    'One-click restock suggestion with quantity, supplier, and expected lead time pre-filled',
  'feature.analytics.f3': 'Dead stock discovery',
  'feature.analytics.f3Desc':
    'Find slow-moving inventory and spot opportunities to free up cash or redistribute.',
  'feature.analytics.f4': 'Restock automation',
  'feature.analytics.f4Desc':
    'Review AI-suggested restocks and approve with one click. Integrate with your PO workflow.',
  'feature.analytics.cta': 'Reduce stock-outs and dead stock with intelligence',

  // Barcode & Mobile Page
  'feature.barcode.tag': 'Barcode & Mobile',
  'feature.barcode.title': 'Inventory confidence, on the go',
  'feature.barcode.subtitle':
    'Generate barcodes in seconds. Scan from any phone. Move inventory fast across warehouses and stores with full visibility and fewer errors.',
  'feature.barcode.problemDesc1':
    'Manual counting and pen-and-paper picking are slow, error-prone, and give no real-time updates to the team.',
  'feature.barcode.problemDesc2':
    'khoai.io mobile app lets your team scan, verify, and confirm every movement instantly from the warehouse floor or store floor.',
  'feature.barcode.bullet1': 'Barcode and QR code generation',
  'feature.barcode.bullet2': 'Mobile app for receiving, picking, and counts',
  'feature.barcode.bullet3': 'Real-time sync and offline mode',
  'feature.barcode.f1': 'Fast barcode printing',
  'feature.barcode.f1Desc':
    'Generate and print barcode or QR code labels for any item directly from khoai.io — in bulk or one at a time.',
  'feature.barcode.f1Bullet1':
    'Print individual or batch label sets in a single action',
  'feature.barcode.f1Bullet2':
    'Customise templates with item name, SKU, unit, and price',
  'feature.barcode.f1Bullet3':
    'Export to any compatible label printer or standard sheet format',
  'feature.barcode.f2': 'Mobile selling workflow',
  'feature.barcode.f2Desc':
    'Sell items directly from mobile with scan-to-add, instant totals, and clean checkout flow for fast-moving counters.',
  'feature.barcode.f2Bullet1':
    'Scan item barcodes to build carts quickly with live price and stock validation',
  'feature.barcode.f2Bullet2':
    'Adjust quantities, apply line discounts, and complete sales without switching screens',
  'feature.barcode.f2Bullet3':
    'Each mobile sale syncs instantly so inventory and revenue stay accurate across the team',
  'feature.barcode.f3': 'Audited mobile action history',
  'feature.barcode.f3Desc':
    'Every mobile sale action is recorded with full timeline context so supervisors can verify what happened and when.',
  'feature.barcode.f3Bullet1':
    'Track order lifecycle events from mobile cart creation to checkout completion',
  'feature.barcode.f3Bullet2':
    'See who performed each action, with timestamps for fast internal review',
  'feature.barcode.f3Bullet3':
    'Open order history instantly from Activity to investigate exceptions or customer disputes',
  'feature.barcode.f4': 'Offline mode',
  'feature.barcode.f4Desc':
    'Continue scanning and working without internet. All data syncs the moment connection returns.',
  'feature.barcode.f4Bullet1':
    'All scans queue locally and sync automatically when connectivity is restored',
  'feature.barcode.f4Bullet2':
    'Works on any Android or iOS device — no special hardware required',
  'feature.barcode.f4Bullet3':
    'Full session history is preserved and reconciled after reconnection',
  'feature.barcode.cta':
    'Speed up warehouse and store operations with mobile-first inventory',

  // Team & Permissions Page
  'feature.team.tag': 'Team & Permissions',
  'feature.team.title': 'Work as one team, with absolute control',
  'feature.team.subtitle':
    'Invite your team. Assign roles. Track every action. All transactions are logged for compliance and operational accountability across warehouses and stores.',
  'feature.team.problemDesc1':
    'Shared access and generic user accounts hide who did what, expose your data to misuse, and make audits a nightmare.',
  'feature.team.problemDesc2':
    'khoai.io gives you role-based access, granular permissions, and a full audit trail so every action is traceable and safe.',
  'feature.team.bullet1': 'Granular role-based access control',
  'feature.team.bullet2': 'Custom roles tailored to your workflow',
  'feature.team.bullet3': 'Complete audit logs of all transactions',
  'feature.team.f1': 'Pre-built roles',
  'feature.team.f1Desc':
    'Admin, Supervisor, Operator, and Viewer roles come ready to use. Assign team members on the go.',
  'feature.team.f1Bullet1':
    'Start with proven role presets that map to common warehouse and store responsibilities',
  'feature.team.f1Bullet2':
    'Assign permissions in seconds when onboarding new staff or temporary workers',
  'feature.team.f1Bullet3':
    'Keep role behavior consistent across all locations and shifts',
  'feature.team.f2': 'Custom roles',
  'feature.team.f2Desc':
    'Create roles with specific permissions for receivers, pickers, counters, or any custom workflow you need.',
  'feature.team.f2Bullet1':
    'Grant access by module and action instead of giving broad, risky privileges',
  'feature.team.f2Bullet2':
    'Design role templates for multi-step operations with clear ownership',
  'feature.team.f2Bullet3':
    'Update permissions safely as processes change without disrupting live work',
  'feature.team.f3': 'Audit log',
  'feature.team.f3Desc':
    'See who created, edited, or deleted every transaction. Export logs for compliance and training.',
  'feature.team.f3Bullet1':
    'Capture a complete action trail for transfers, adjustments, and orders',
  'feature.team.f3Bullet2':
    'Filter by user, action type, and date range to investigate incidents quickly',
  'feature.team.f3Bullet3':
    'Use clear history records to support compliance checks and team coaching',
  'feature.team.f4': 'Invite with email',
  'feature.team.f4Desc':
    'Send invitations to team members instantly. They accept and start working right away.',
  'feature.team.f4Bullet1':
    'Invite staff by work email and keep account ownership under your business domain',
  'feature.team.f4Bullet2':
    'Apply the correct role before first login so access is safe from day one',
  'feature.team.f4Bullet3':
    'Track invitation status to follow up on pending members without manual spreadsheets',
  'feature.team.cta':
    'Build safer, compliant inventory workflows across warehouses and stores with your team',

  // FAQ sections — feature pages
  'feature.inventory.faq.title': 'Frequently asked questions',
  'feature.inventory.faq.q1':
    'Can khoai.io manage inventory across multiple warehouses and stores?',
  'feature.inventory.faq.a1':
    'Yes. khoai.io is built for multi-location operations. You get per-location stock dashboards, location-specific transfers, and access controls so each warehouse or store team sees only what they need.',
  'feature.inventory.faq.q2': 'How does khoai.io track stock adjustments?',
  'feature.inventory.faq.a2':
    'Every quantity change runs as an atomic transaction and logs a typed reason — sale, return, damage, theft, manual count, or supplier delivery — along with who made the change, when, and the before and after quantities.',
  'feature.inventory.faq.q3':
    'Can I import my existing inventory from a spreadsheet?',
  'feature.inventory.faq.a3':
    'Yes. Just upload your existing spreadsheet and khoai.io maps your columns, validates every row, and loads your inventory without any reformatting.',
  'feature.inventory.faq.q4': 'How do inter-warehouse transfers work?',
  'feature.inventory.faq.a4':
    'Transfers follow a full lifecycle from pending to in-transit to received. Teams can track each step, record discrepancies on arrival such as damaged or missing items, and review overdue transfers from a dedicated exception inbox.',
  'feature.inventory.faq.q5': 'Does khoai.io support partial receiving?',
  'feature.inventory.faq.a5':
    'Yes. You can receive a transfer partially and log the reason for any discrepancy. The remaining quantity stays in-transit until fully received or cancelled.',

  'feature.analytics.faq.title': 'Frequently asked questions',
  'feature.analytics.faq.q1': 'How does khoai.io predict inventory demand?',
  'feature.analytics.faq.a1':
    'khoai.io analyses your historical sales data, seasonal patterns, and location-specific demand velocity to generate per-SKU demand curves. Forecasts update continuously as new sales data arrives.',
  'feature.analytics.faq.q2':
    'What types of alerts does the analytics feature provide?',
  'feature.analytics.faq.a2':
    'You get low-stock alerts ranked by severity — critical, warning, and watch — plus dead stock signals for items that have not moved within your configured window. Each alert includes a one-click restock suggestion.',
  'feature.analytics.faq.q3': 'Can khoai.io help me reduce dead stock?',
  'feature.analytics.faq.a3':
    'Yes. The dead stock discovery module surfaces slow-moving items across all locations, so you can take action — markdowns, redistribution, or supplier returns — before capital is locked up further.',
  'feature.analytics.faq.q4':
    'Does the AI forecasting work for seasonal businesses?',
  'feature.analytics.faq.a4':
    'Yes. The forecasting engine recognises seasonal and event-based patterns across your full item catalogue and adjusts thresholds accordingly, so alerts remain relevant during peak and off-peak periods.',
  'feature.analytics.faq.q5': 'How often is the demand forecast updated?',
  'feature.analytics.faq.a5':
    'Forecasts are recalculated continuously as new transactions are recorded, so the recommendations you see always reflect your most recent sales activity.',

  'feature.barcode.faq.title': 'Frequently asked questions',
  'feature.barcode.faq.q1': 'Does the khoai.io mobile app work offline?',
  'feature.barcode.faq.a1':
    'Yes. All scans and actions queue locally on the device and sync automatically the moment internet connectivity is restored. No data is lost between sessions.',
  'feature.barcode.faq.q2': 'Which mobile devices are supported?',
  'feature.barcode.faq.a2':
    'The khoai.io mobile app runs on any Android or iOS device. No special barcode hardware is required — the built-in camera handles all scanning.',
  'feature.barcode.faq.q3': 'How do I print barcode labels with khoai.io?',
  'feature.barcode.faq.a3':
    'Select one item or a batch from your inventory list, choose a label template, and print directly to any compatible label printer or standard sheet format. Label templates support item name, SKU, unit, and price.',
  'feature.barcode.faq.q4':
    'Can my staff use their own phones to scan inventory?',
  'feature.barcode.faq.a4':
    'Yes. Each team member installs the khoai.io app on their own Android or iOS device and logs in with their role-based account. All scans sync to the shared inventory in real time.',
  'feature.barcode.faq.q5': 'Does mobile selling sync with the main inventory?',
  'feature.barcode.faq.a5':
    'Yes. Every sale completed on mobile updates inventory quantities and revenue figures instantly across all connected devices and the web dashboard.',

  'feature.team.faq.title': 'Frequently asked questions',
  'feature.team.faq.q1': 'Can I create custom roles in khoai.io?',
  'feature.team.faq.a1':
    'Yes. In addition to the built-in Admin, Supervisor, Operator, and Viewer roles, you can create custom roles with permissions set per module and action. This lets you tailor access for receivers, pickers, counters, or any workflow-specific position.',
  'feature.team.faq.q2': 'How do I invite team members?',
  'feature.team.faq.a2':
    'Send an invitation by work email from the Team settings page. The invitee receives a link, accepts, and their account is activated under your business with the role you assigned — ready to work from first login.',
  'feature.team.faq.q3': 'Does khoai.io keep an audit log of all actions?',
  'feature.team.faq.a3':
    'Yes. Every transaction — transfer, adjustment, order, role change — is logged with the user, action type, timestamp, and before/after state. Logs can be filtered and exported for compliance reviews or internal training.',
  'feature.team.faq.q4':
    'Can I restrict access by warehouse or store location?',
  'feature.team.faq.a4':
    'Yes. Location-based access is built into the permission model, so you can give a warehouse team full access to their own location while keeping other locations private.',
  'feature.team.faq.q5': 'How many team members can I add?',
  'feature.team.faq.a5':
    'The number of seats depends on your plan. See the Pricing page for per-plan limits. You can always adjust your plan as your team grows.',

  // Shared UI labels
  'public.nav.overview': 'Overview',
  'public.placeholder.visual': 'Product visual placeholder',
  'public.placeholder.swapLater': 'Swap with screenshot later',

  // Additional Landing labels
  'landing.founder.comingSoon': 'Coming soon',
  'landing.dashboard.storefrontHub': 'Storefront Hub',
  'landing.dashboard.left': 'left',
  'landing.dashboard.restockSuggestion':
    'Move 24 units of B-440 Sensor from Warehouse B to A and create PO for 120 units to avoid projected stockout by Friday.',
  'landing.feature.section1.retailFront': 'Retail Front',
  'landing.feature.section2.reorderForecast':
    'Inventory forecasting indicates reorder in 4 days for Warehouse A.',

  // Feature page visuals and per-page CTA subtitles
  'feature.inventory.visual1Title': 'Inventory list and stock summary',
  'feature.inventory.visual1Caption':
    "Live item list with storehouse filter, status breakdown (in-stock / low-stock / out-of-stock), and today's sales summary.",
  'feature.inventory.visual2Title': 'Transfer status and exception inbox',
  'feature.inventory.visual2Caption':
    'Transfer lifecycle view showing pending, in-transit, and partially-received shipments — with an exception inbox for overdue transfers.',
  'feature.inventory.visual3Title': 'Adjustment history per SKU',
  'feature.inventory.visual3Caption':
    'Per-item adjustment log with typed reasons, timestamps, and quantity deltas — every change is an auditable event.',
  'feature.inventory.ctaSubtitle':
    "Get live stock counts, full adjustment history, and multi-location transfers in one operational system. Free plan covers one location and 500 items. Scale up when you're ready.",

  'feature.analytics.visual1Title': 'Analytics overview screenshot',
  'feature.analytics.visual1Caption':
    'Future product screenshot area: trend chart, low-stock risk panel, and forecasting widgets.',
  'feature.analytics.visual2Title': 'Low-stock smart alerts screenshot',
  'feature.analytics.visual2Caption':
    'Future product screenshot area: alert queue with severity, location, and recommended action.',
  'feature.analytics.visual3Title': 'Forecast and recommendation screenshot',
  'feature.analytics.visual3Caption':
    'Future product screenshot area: forecast chart with AI-assisted restock recommendations.',
  'feature.analytics.ctaSubtitle':
    'Give your operations team the insights they need to protect margin and product availability across warehouses, stores, and high-audit product lines.',

  'feature.barcode.visual1Title': 'Barcode generation workflow',
  'feature.barcode.visual1Caption':
    'Create labels from low-stock queues, verify template mapping, and push batches to print without manual spreadsheets.',
  'feature.barcode.visual2Title': 'Mobile sales cart and checkout flow',
  'feature.barcode.visual2Caption':
    'Build a sale by scanning items, adjust quantities, and complete checkout with synced stock and revenue updates.',
  'feature.barcode.visual3Title': 'Mobile order history and audit timeline',
  'feature.barcode.visual3Caption':
    'Activity order-history view showing recorded actions, timestamps, and actor details for each mobile order.',
  'feature.barcode.ctaSubtitle':
    'Equip teams with mobile tools and barcode precision to reduce errors and improve execution speed.',

  'feature.team.visual1Title': 'Role and permission matrix screenshot',
  'feature.team.visual1Caption':
    'Future product screenshot area: role permissions matrix and user assignment workflow.',
  'feature.team.visual2Title': 'User activity timeline screenshot',
  'feature.team.visual2Caption':
    'Future product screenshot area: per-user activity feed with action metadata and timestamps.',
  'feature.team.visual3Title': 'Permission policy editor screenshot',
  'feature.team.visual3Caption':
    'Future product screenshot area: role editor with module-level access controls and previews.',
  'feature.team.visual4Title': 'Team invitation and member status screenshot',
  'feature.team.visual4Caption':
    'Future product screenshot area: invitation flow, pending members, and role assignment before activation.',
  'feature.team.ctaSubtitle':
    'Keep teams productive with the right access model while protecting business-critical inventory workflows.',

  // SEO page descriptions
  'seo.landing.desc':
    'Prevent stockouts and lost sales with khoai.io — real-time inventory and stock management for teams across multiple warehouses and stores. Set restock alerts, scan barcodes, and manage transfers. Free for 1 location.',
  'seo.pricing.desc':
    'Simple, transparent pricing for teams of all sizes. Start free and scale as your business grows.',
  'seo.privacy.desc':
    'Learn how khoai.io collects, uses, and protects your personal data.',
  'seo.terms.desc':
    'The terms governing your use of khoai.io — the inventory and store management platform.',
  'seo.billingTerms.desc':
    'Billing terms, subscription details, refund policies, and accepted payment methods for khoai.io.',
};
